import React, { useEffect, useState, useRef } from "react";
import * as Tone from "tone";
import { useDAWStore, INSTRUMENT_DEFS } from "../../store/dawStore";
import engine from "../../audio/AudioEngine";
import { decodeAudioFile } from "../../audio/decodeAudio";
import { Plus, Trash, MagnifyingGlassPlus, MagnifyingGlassMinus, X, ArrowsOutLineHorizontal, ArrowsOutLineVertical, GearSix, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Arranger() {
  const {
    tracks, addTrack, removeTrack, updateTrack, setTrackColor, assignInstrument,
    toggleMute, toggleSolo,
    regions, addRegion, removeRegion, updateRegion, duplicateRegion,
    selectedRegionId, selectRegion,
    selectedIds, setSelectedIds, clearSelection,
    selectedTrackId, selectTrack,
    totalBars, isPlaying, activePlugin, setActivePlugin, setActiveTab,
    addFxToTrack, masterFxChain, masterVolume,
    addAudioClip, updateAudioClip, removeAudioClip,
    snapEnabled, setSnapEnabled, snapDivision, setSnapDivision,
    toolMode, setToolMode,
    duplicateTrack,
    reverseAudioClip,
    toggleTrackAutomation, addAutomationNode, updateAutomationNode, removeAutomationNode,
    toggleRecordArm,
    reorderTracks,
  } = useDAWStore();

  const [pixelsPerBar, setPixelsPerBar] = useState(180);
  const [trackHeight, setTrackHeight] = useState(56);
  const [showGridControls, setShowGridControls] = useState(false);
  const [dragOverTrackId, setDragOverTrackId] = useState(null);
  const [stretchTooltip, setStretchTooltip] = useState(null); // v19.24 — {clipId, ratio} during Alt-drag time-stretch
  // v19.10 — Track-header drag-reorder state. `id` = the track being
  // dragged; `over` = the track currently under the pointer; `pos` =
  // "before" / "after" for the drop indicator line.
  const [trackDrag, setTrackDrag] = useState(null);
  const [marquee, setMarquee] = useState(null); // {x1,y1,x2,y2}
  const marqueeRef = useRef(null);
  const tracksScrollRef = useRef(null);
  const timelineScrollRef = useRef(null); // top-ruler scroller — synced to tracksScrollRef.scrollLeft
  const loopStripScrollRef = useRef(null); // v19.22 — loop strip scroller — also synced so the loop marker stays locked to its bars
  const clipboardRef = useRef(null); // { items: [...], minBar: number }
  // Loop region: { start: bars, end: bars } or null
  const [loop, setLoop] = useState(null);
  const [dragLoopEnd, setDragLoopEnd] = useState(null);
  // v14.0.1 — playheadPos state REMOVED from Arranger. Previously the
  // 60Hz RAF setPlayheadPos re-rendered the ENTIRE Arranger tree (every
  // track lane, every region, every canvas) sixty times a second during
  // playback, which stacked with the giant-DOM zoom cost and made
  // stretched-grid playback feel laggy. The playhead is now a
  // self-updating <PlayheadLine> component with its OWN RAF loop that
  // only mutates its own `left` style — the rest of the tree stays
  // completely still while Transport advances.

  // v10.1 — Apply loop to Tone Transport AND snap the playhead to the
  // loop start whenever the loop region changes. Previously the transport
  // remembered its old position, so dragging the loop to bars 7-9 still
  // played from bar 1 until the loop wrapped around. Now the green
  // playhead jumps to the loop start instantly, matching standard DAW
  // (Logic / Ableton / FL Studio) behavior.
  useEffect(() => {
    if (!engine.initialized) return;
    const t = Tone.getTransport();
    if (loop && loop.end > loop.start) {
      // v17.1 — record the DAW-owned loop on the engine so engine.play()
      // knows NOT to clear it even after the GenPlayer ran (GenPlayer
      // uses the same global Transport for its 1-bar preview loop).
      engine._dawLoopActive = true;
      t.loop = true;
      t.loopStart = `${loop.start}m`;
      t.loopEnd = `${loop.end}m`;
      try {
        const startSec = Tone.Time(`${loop.start}m`).toSeconds();
        const endSec   = Tone.Time(`${loop.end}m`).toSeconds();
        if (t.seconds < startSec || t.seconds >= endSec) {
          t.seconds = startSec;
        }
      } catch { /* time-conversion failure — leave transport as-is */ }
    } else {
      // v17.1 — CLEAR the DAW loop flag AND the transport's loop state
      // whenever the user clears the blue Loop Region, so a stray 1-bar
      // loop left behind by the beat generator can't force the DAW
      // arrangement to loop bar 1 forever.
      engine._dawLoopActive = false;
      t.loop = false;
    }
  }, [loop]);

  // v19.22 — Playhead auto-follow. During playback, keep the playhead
  // visible in the arranger viewport by scrolling horizontally when
  // it approaches the right edge. Standard DAW behavior (Logic /
  // Ableton / FL Studio all default to this).
  //
  // Strategy:
  //   • RAF-driven poll while isPlaying (already the cheapest tick we
  //     have — the PlayheadLine also uses RAF). Each frame we read
  //     Tone.Transport.seconds → convert to px → compare against the
  //     tracks container's visible window. When the playhead crosses
  //     ~70% of the visible width, we snap the scroll left so the
  //     playhead re-enters the LEFT 20% of the visible area — this
  //     matches the "chase" pattern producers expect (fast catch-up,
  //     then long visible run before the next scroll).
  //   • Skips scrolling if the user is actively dragging the scroll
  //     bar themselves (detected via a short cooldown after any
  //     manual `scroll` event that WASN'T from us). Producers who
  //     want to inspect a specific bar during playback shouldn't be
  //     fought by the auto-follow.
  //   • Uses `scrollTo({behavior:"auto"})` — the RAF cadence itself
  //     is smooth enough; "smooth" scroll behavior would cascade
  //     across frames and feel laggy.
  useEffect(() => {
    if (!isPlaying) return;
    const container = tracksScrollRef.current;
    if (!container) return;
    let raf = 0;
    let stopped = false;
    let userScrollLockUntil = 0;
    // Only treat a scroll event as "user-initiated" if we didn't just
    // set scrollLeft ourselves.
    let selfSetScrollLeft = null;
    const onScroll = () => {
      if (selfSetScrollLeft !== null && Math.abs(container.scrollLeft - selfSetScrollLeft) < 2) {
        selfSetScrollLeft = null;
        return;
      }
      userScrollLockUntil = performance.now() + 1500; // 1.5s pause after user grabs the scroll bar
      selfSetScrollLeft = null;
    };
    container.addEventListener("scroll", onScroll, { passive: true });
    const tick = () => {
      if (stopped) return;
      if (performance.now() < userScrollLockUntil) {
        raf = requestAnimationFrame(tick);
        return;
      }
      try {
        const t = Tone.getTransport();
        const sec = t.seconds || 0;
        const bpm = (t.bpm && t.bpm.value) || 120;
        // 4 beats per bar; playhead px offset inside the scrollable region.
        const bars = (sec * bpm) / 60 / 4;
        const playheadPx = bars * pixelsPerBar;
        const scrollLeft = container.scrollLeft;
        const viewportW = container.clientWidth;
        // The sticky 210px track-header strip covers the leftmost part
        // of the viewport; playhead is only visible past that column,
        // so treat the header as consumed viewport width.
        const HEADER_W = 210;
        const visibleLeft = scrollLeft + HEADER_W;
        const visibleRight = scrollLeft + viewportW;
        const chaseTrigger = scrollLeft + HEADER_W + (viewportW - HEADER_W) * 0.7; // ~70% into the visible run
        const chaseLandingLeft = playheadPx - (viewportW - HEADER_W) * 0.2 - HEADER_W;
        // Chase forward: playhead crossed the 70% mark → jump so it lands ~20% in.
        if (playheadPx > chaseTrigger) {
          const target = Math.max(0, chaseLandingLeft);
          selfSetScrollLeft = target;
          container.scrollLeft = target;
          // Mirrored to timeline / loop strip via the existing onScroll
          // handler on the tracks container (already in place).
        }
        // Chase backward: playhead somehow trailed behind the visible
        // area (loop wrap, manual playhead scrub) → snap so it lands
        // ~20% into the visible run.
        else if (playheadPx < visibleLeft - 10) {
          const target = Math.max(0, chaseLandingLeft);
          selfSetScrollLeft = target;
          container.scrollLeft = target;
        }
      } catch { /* transport not ready yet */ }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      container.removeEventListener("scroll", onScroll);
    };
  }, [isPlaying, pixelsPerBar]);


  // v14.0.1 — The Arranger-level playhead RAF was DELETED here (used to
  // setPlayheadPos 60x/s, forcing a full tree re-render). The
  // <PlayheadLine> component below now runs its own isolated RAF and
  // only mutates its OWN inline style, so the arranger tree stays
  // perfectly still during playback. See its definition near the
  // bottom of this file.

  const handleAddRegion = (trackId) => {
    const usedBars = regions.filter((r) => r.trackId === trackId).map((r) => r.bar);
    let bar = 0;
    while (usedBars.includes(bar) && bar < totalBars) bar++;
    if (bar >= totalBars) bar = 0;
    addRegion(trackId, bar, 1);
  };

  // Scissor: split a region (MIDI) at an absolute bar position.
  const splitRegion = (region, atBar) => {
    if (atBar <= region.bar + 0.01 || atBar >= region.bar + region.length - 0.01) return;
    const rightLen = (region.bar + region.length) - atBar;
    updateRegion(region.id, { length: atBar - region.bar });
    const newId = `r${Date.now()}_${Math.random().toString(36).slice(2,7)}`;
    useDAWStore.setState((state) => ({
      regions: [...state.regions, {
        ...region,
        id: newId,
        bar: atBar,
        length: rightLen,
        pattern: [...(region.pattern || new Array(16).fill(false))],
        notes: (region.notes || []).map((n) => ({ ...n, id: `n${Date.now()}_${Math.random().toString(36).slice(2,5)}` })),
      }],
    }));
    toast.success("Region split");
  };

  // Scissor: split an audio clip — second half's offset advances by the cut length
  // so the same buffer continues seamlessly from the cut point.
  const splitAudioClip = (track, clip, atBar) => {
    if (atBar <= clip.bar + 0.01 || atBar >= clip.bar + clip.length - 0.01) return;
    const secPerBar = (60 / Math.max(1, engine.getBpm() || 120)) * 4;
    const leftLen = atBar - clip.bar;
    const rightLen = (clip.bar + clip.length) - atBar;
    updateAudioClip(track.id, clip.id, { length: leftLen });
    const newId = `ac${Date.now()}_${Math.random().toString(36).slice(2,6)}`;
    const newOffset = (clip.offset || 0) + leftLen * secPerBar;
    addAudioClip(track.id, { ...clip, id: newId, bar: atBar, length: rightLen, offset: newOffset });
    if (clip.url) engine.loadAudioClip(track.id, newId, clip.url);
    toast.success("Audio split");
  };

  // v11.3 — Track compatibility for cross-track region drops.
  // Drum tracks (kick/snare/hihat/clap type OR sampler with drumRole)
  // and audio tracks are locked to their lane so a piano melody can't
  // be dropped onto a kick. Melodic tracks (synth, non-drum sampler,
  // bass, melody) can swap regions freely between themselves.
  const isDrumRoleTrack = (t) => {
    if (!t) return false;
    if (t.type === "audio") return true; // audio never accepts MIDI drops
    const DRUM_TYPES = new Set(["kick", "snare", "hihat", "clap"]);
    if (DRUM_TYPES.has(t.type)) return true;
    if (t.type === "sampler" && DRUM_TYPES.has(t.samplerRole)) return true;
    return false;
  };
  const canDropRegionOn = (sourceTrack, targetTrack) => {
    if (!sourceTrack || !targetTrack) return false;
    if (sourceTrack.id === targetTrack.id) return true;
    if (targetTrack.type === "audio") return false;
    return isDrumRoleTrack(sourceTrack) === isDrumRoleTrack(targetTrack);
  };

  const handleRegionMouseDown = (e, region) => {
    e.stopPropagation();
    // Scissor tool: click → split at click position (snap-aware)
    if (toolMode === "scissor") {
      const rect = e.currentTarget.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetBars = offsetX / pixelsPerBar;
      let atBar = region.bar + offsetBars;
      if (snapEnabled) {
        const snapStep = 1 / Math.max(1, snapDivision);
        atBar = Math.round(atBar / snapStep) * snapStep;
      }
      splitRegion(region, atBar);
      return;
    }

    const isResize = e.target.dataset?.resize === "true";

    // v19.21 — Multi-drag detection. If this region is part of an
    // active multi-selection (2+ regions selected) AND we're NOT
    // resizing, drag ALL selected regions as a group preserving their
    // relative bar offsets + track assignments. Standard DAW behaviour.
    //
    // Note: multi-drag is horizontal-only for now — vertical (cross-
    // track) reassignment stays single-region so the operator can't
    // accidentally shove drum regions onto an audio-only lane. Users
    // who want to move a group across tracks can grab clips one at a
    // time (rare workflow); the common case is aligning a chorus /
    // verse block across the timeline, which this handles.
    const inMultiSel = !isResize
      && Array.isArray(selectedIds)
      && selectedIds.length >= 2
      && selectedIds.includes(region.id);

    if (inMultiSel) {
      // Freeze the starting positions of every selected region so the
      // group moves as one unit regardless of intra-drag re-renders.
      const groupSnapshot = regions
        .filter((r) => selectedIds.includes(r.id))
        .map((r) => ({ id: r.id, startBar: r.bar, length: r.length, trackId: r.trackId }));
      if (groupSnapshot.length === 0) return;
      const startX = e.clientX;
      const anchor = groupSnapshot.find((g) => g.id === region.id) || groupSnapshot[0];
      // Store: use setState with a single map to update every region
      // in one commit — one re-render per frame instead of N.
      let rafId = null;
      let pendingDeltaBars = null;
      const flush = () => {
        rafId = null;
        if (pendingDeltaBars === null) return;
        const d = pendingDeltaBars;
        pendingDeltaBars = null;
        useDAWStore.setState((state) => {
          const snapMap = new Map(groupSnapshot.map((g) => [g.id, g]));
          return {
            regions: state.regions.map((r) => {
              const snap = snapMap.get(r.id);
              if (!snap) return r;
              const newBar = Math.max(0, Math.min(totalBars - snap.length, snap.startBar + d));
              return { ...r, bar: newBar };
            }),
          };
        });
      };
      const schedule = (d) => {
        pendingDeltaBars = d;
        if (rafId === null) rafId = requestAnimationFrame(flush);
      };
      const onMove = (ev) => {
        const dx = ev.clientX - startX;
        let dBars = dx / pixelsPerBar;
        if (snapEnabled) {
          const snapStep = 1 / Math.max(1, snapDivision);
          // Snap the ANCHOR's new bar to the grid, then derive the
          // group delta from that — so every selected region snaps in
          // lockstep on the same grid line the mouse is over.
          const snappedAnchorBar = Math.round((anchor.startBar + dBars) / snapStep) * snapStep;
          dBars = snappedAnchorBar - anchor.startBar;
        }
        // Clamp delta so no region in the group escapes the timeline
        // on either side — otherwise the group would silently
        // "compress" when one edge hit bar 0 while others slid right.
        let minStart = Infinity, maxEnd = -Infinity;
        for (const g of groupSnapshot) {
          if (g.startBar < minStart) minStart = g.startBar;
          if (g.startBar + g.length > maxEnd) maxEnd = g.startBar + g.length;
        }
        const minDelta = -minStart;                // can't move earliest region below bar 0
        const maxDelta = totalBars - maxEnd;       // can't push latest region past totalBars
        dBars = Math.max(minDelta, Math.min(maxDelta, dBars));
        schedule(dBars);
      };
      const onUp = () => {
        if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
        // Flush any pending delta on release so the final resting
        // position matches what the user let go on.
        if (pendingDeltaBars !== null) { const d = pendingDeltaBars; pendingDeltaBars = null; useDAWStore.setState((state) => {
          const snapMap = new Map(groupSnapshot.map((g) => [g.id, g]));
          return {
            regions: state.regions.map((r) => {
              const snap = snapMap.get(r.id);
              if (!snap) return r;
              const newBar = Math.max(0, Math.min(totalBars - snap.length, snap.startBar + d));
              return { ...r, bar: newBar };
            }),
          };
        }); }
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
      return;
    }

    // Single-region path (original behaviour).
    selectRegion(region.id);

    const startX = e.clientX;
    const startY = e.clientY;
    const startBar = region.bar;
    const startLength = region.length;
    const sourceTrack = tracks.find((t) => t.id === region.trackId);
    const DRAG_THRESHOLD_PX = 3;

    // v12.0 — RAF-throttle store updates during drag. Without this, a
    // 120Hz mouse would push ~120 setState calls/sec → Arranger re-render
    // per frame → visible stutter with many regions. We coalesce moves
    // into ONE store write per rAF (~60fps max) so the store no longer
    // thrashes and the drag feels butter-smooth.
    let pendingPatch = null;
    let rafId = null;
    let pendingTrackId = null;
    const flushPending = () => {
      rafId = null;
      if (pendingPatch) {
        updateRegion(region.id, pendingPatch);
        pendingPatch = null;
      }
    };
    const schedule = (patch) => {
      pendingPatch = pendingPatch ? { ...pendingPatch, ...patch } : patch;
      if (rafId === null) rafId = requestAnimationFrame(flushPending);
    };

    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const dBars = dx / pixelsPerBar;
      const useSnap = snapEnabled;
      const snapStep = 1 / Math.max(1, snapDivision);
      if (isResize) {
        let newLen = startLength + dBars;
        newLen = useSnap ? Math.max(snapStep, Math.round(newLen / snapStep) * snapStep) : Math.max(0.05, newLen);
        if (region.bar + newLen <= totalBars) schedule({ length: newLen });
      } else {
        let newBar = startBar + dBars;
        newBar = useSnap ? Math.round(newBar / snapStep) * snapStep : newBar;
        newBar = Math.max(0, Math.min(totalBars - region.length, newBar));
        schedule({ bar: newBar });

        // Vertical drag: hit-test target lane (unchanged logic).
        if (Math.abs(dy) < DRAG_THRESHOLD_PX && Math.abs(dx) < DRAG_THRESHOLD_PX) return;
        let targetTrackId = null;
        for (const t of tracks) {
          const el = document.querySelector(`[data-testid="track-grid-${t.id}"]`);
          if (!el) continue;
          const r = el.getBoundingClientRect();
          if (ev.clientY >= r.top && ev.clientY <= r.bottom) {
            targetTrackId = t.id;
            break;
          }
        }
        if (!targetTrackId || targetTrackId === region.trackId) {
          if (pendingTrackId !== null) { pendingTrackId = null; setDragOverTrackId(null); }
          return;
        }
        const targetTrack = tracks.find((tt) => tt.id === targetTrackId);
        if (canDropRegionOn(sourceTrack, targetTrack)) {
          if (pendingTrackId !== targetTrackId) {
            pendingTrackId = targetTrackId;
            setDragOverTrackId(targetTrackId);
          }
          schedule({ trackId: targetTrackId });
        } else {
          setDragOverTrackId(`__block_${targetTrackId}`);
        }
      }
    };
    const onUp = () => {
      // Flush any pending patch on mouse-up so the final position isn't lost.
      if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
      if (pendingPatch) { updateRegion(region.id, pendingPatch); pendingPatch = null; }
      setDragOverTrackId(null);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      const ids = selectedIds && selectedIds.length > 0
        ? selectedIds
        : (selectedRegionId ? [selectedRegionId] : []);

      if ((e.key === "Delete" || e.key === "Backspace") && ids.length > 0) {
        e.preventDefault();
        ids.forEach((id) => {
          const region = regions.find((r) => r.id === id);
          if (region) { removeRegion(id); return; }
          for (const t of tracks) {
            const clip = (t.audioClips || []).find((c) => c.id === id);
            if (clip) { removeAudioClip(t.id, clip.id); return; }
          }
        });
        clearSelection();
        if (ids.length > 1) toast.success(`Deleted ${ids.length} items`);
        return;
      }

      // Ctrl/Cmd + D = duplicate selection immediately after original(s).
      // v19.15 FIX — Always preventDefault so the browser bookmark dialog
      // never opens on Cmd+D inside the DAW (was firing whenever nothing
      // was selected → user thought duplicate was broken). Single-region
      // path routes through the store's `duplicateRegion` which places
      // the copy right after the source on the same track AND walks past
      // any other region occupying that slot so overlaps are impossible.
      if ((e.ctrlKey || e.metaKey) && (e.key === "d" || e.key === "D")) {
        e.preventDefault();
        if (ids.length === 0) return;
        // Resolve each id to its region or audio clip + bounds.
        const items = [];
        let selStart = Infinity, selEnd = -Infinity;
        ids.forEach((id) => {
          const region = regions.find((r) => r.id === id);
          if (region) {
            items.push({ kind: "region", ref: region });
            selStart = Math.min(selStart, region.bar);
            selEnd = Math.max(selEnd, region.bar + region.length);
            return;
          }
          for (const t of tracks) {
            const clip = (t.audioClips || []).find((c) => c.id === id);
            if (clip) {
              items.push({ kind: "audio", trackId: t.id, ref: clip });
              selStart = Math.min(selStart, clip.bar);
              selEnd = Math.max(selEnd, clip.bar + clip.length);
              break;
            }
          }
        });
        if (items.length === 0) return;
        const newIds = [];
        // Single region: delegate to store's overlap-aware duplicateRegion so
        // the copy lands directly after the source (bar 1 → bar 2) and shoves
        // past any pre-existing regions on the same track.
        if (items.length === 1 && items[0].kind === "region") {
          const r = items[0].ref;
          const state = useDAWStore.getState();
          const beforeCount = state.regions.length;
          state.duplicateRegion(r.id);
          const after = useDAWStore.getState().regions;
          if (after.length > beforeCount) {
            const created = after[after.length - 1];
            newIds.push(created.id);
          }
        } else {
          // Multi-selection or audio clips: span-based shift, per-track
          // overlap dodge so drum lanes don't get their duplicates
          // stacked on top of pre-existing regions.
          const span = Math.max(0.05, selEnd - selStart);
          items.forEach((it) => {
            if (it.kind === "region") {
              const r = it.ref;
              let newBar = Math.max(0, Math.min(totalBars - r.length, r.bar + span));
              // Overlap dodge on same track.
              const state = useDAWStore.getState();
              const sameTrack = state.regions
                .filter((x) => x.trackId === r.trackId && !ids.includes(x.id))
                .sort((a, b) => a.bar - b.bar);
              for (const other of sameTrack) {
                if (newBar + r.length <= other.bar) break;
                if (newBar < other.bar + other.length) newBar = other.bar + other.length;
              }
              if (newBar + r.length > totalBars) newBar = Math.max(0, totalBars - r.length);
              const newId = `r${Date.now()}_${Math.random().toString(36).slice(2,7)}`;
              useDAWStore.setState((state) => ({
                regions: [...state.regions, {
                  ...r,
                  id: newId,
                  bar: newBar,
                  // v19.25 — Inherit source name verbatim (no "copy" suffix).
                  pattern: [...(r.pattern || new Array(16).fill(false))],
                  notes: (r.notes || []).map((n) => ({ ...n, id: `n${Date.now()}_${Math.random().toString(36).slice(2,5)}` })),
                }],
              }));
              newIds.push(newId);
            } else {
              const clip = it.ref;
              let newBar = Math.max(0, Math.min(totalBars - clip.length, clip.bar + span));
              const state = useDAWStore.getState();
              const trk = state.tracks.find((t) => t.id === it.trackId);
              const sameTrackClips = ((trk?.audioClips) || [])
                .filter((c) => !ids.includes(c.id))
                .sort((a, b) => a.bar - b.bar);
              for (const other of sameTrackClips) {
                if (newBar + clip.length <= other.bar) break;
                if (newBar < other.bar + other.length) newBar = other.bar + other.length;
              }
              if (newBar + clip.length > totalBars) newBar = Math.max(0, totalBars - clip.length);
              const newId = `ac${Date.now()}_${Math.random().toString(36).slice(2,6)}`;
              addAudioClip(it.trackId, { ...clip, id: newId, bar: newBar });
              if (clip.url) engine.loadAudioClip(it.trackId, newId, clip.url);
              newIds.push(newId);
            }
          });
        }
        if (newIds.length > 0) {
          setSelectedIds(newIds);
          toast.success(newIds.length > 1 ? `Duplicated ${newIds.length} items` : "Duplicated");
        }
        return;
      }

      // Ctrl/Cmd + C = copy selection to virtual clipboard
      if ((e.ctrlKey || e.metaKey) && (e.key === "c" || e.key === "C")) {
        if (ids.length > 0) {
          e.preventDefault();
          const items = [];
          let minBar = Infinity;
          ids.forEach((id) => {
            const region = regions.find((r) => r.id === id);
            if (region) {
              items.push({
                kind: "region",
                trackId: region.trackId,
                bar: region.bar,
                length: region.length,
                name: region.name,
                pattern: [...(region.pattern || new Array(16).fill(false))],
                notes: (region.notes || []).map((n) => ({ ...n })),
              });
              if (region.bar < minBar) minBar = region.bar;
              return;
            }
            for (const t of tracks) {
              const clip = (t.audioClips || []).find((c) => c.id === id);
              if (clip) {
                items.push({
                  kind: "audio",
                  trackId: t.id,
                  bar: clip.bar,
                  length: clip.length,
                  url: clip.url,
                  name: clip.name,
                  peaks: clip.peaks,
                  peaksFormat: clip.peaksFormat,
                  duration: clip.duration,
                  pitch: clip.pitch || 0,
                  reversed: clip.reversed || false,
                });
                if (clip.bar < minBar) minBar = clip.bar;
                break;
              }
            }
          });
          if (items.length > 0 && Number.isFinite(minBar)) {
            clipboardRef.current = { items, minBar };
            toast.success(`Copied ${items.length} item${items.length > 1 ? "s" : ""}`);
          }
          return;
        }
      }

      // Ctrl/Cmd + V = paste clipboard at current playhead position.
      // v19.15 — If the playhead is still at bar 0 (transport hasn't been
      // moved by ruler click OR playback), the paste MUST NOT overlap the
      // source: fall back to "after original" placement so a Cmd+C /
      // Cmd+V pair behaves like Cmd+D. If the playhead is anywhere else,
      // paste lands exactly at the playhead (the clipboard's earliest
      // item snaps to it and later items keep their relative offsets).
      //
      // v19.15 FIX — Read Tone.getTransport().seconds directly (same
      // source PlayheadLine uses) instead of engine.getSeconds() which
      // is guarded on `engine.initialized` (false until the first Play).
      // Previously the visible playhead and the paste target diverged
      // before the first Play — user saw the green line at bar 5 but
      // the paste dropped at bar 2.
      if ((e.ctrlKey || e.metaKey) && (e.key === "v" || e.key === "V")) {
        const cb = clipboardRef.current;
        if (!cb || !cb.items.length) return;
        e.preventDefault();
        let sec = 0, bpm = 120;
        try {
          const t = Tone.getTransport();
          sec = t.seconds || 0;
          bpm = (t.bpm && t.bpm.value) || 120;
        } catch { /* transport not ready — treat as bar 0 */ }
        let playheadBars = (sec * bpm) / 60 / 4;
        if (snapEnabled) {
          const snapStep = 1 / Math.max(1, snapDivision);
          playheadBars = Math.round(playheadBars / snapStep) * snapStep;
        }
        // If the playhead never left bar 0, place the paste directly
        // after the clipboard's rightmost item so we never overlap the
        // original selection.
        const cbSpan = cb.items.reduce((acc, it) => Math.max(acc, it.bar + it.length), 0) - cb.minBar;
        const useAfterOriginal = playheadBars < 0.001;
        const offset = useAfterOriginal ? cbSpan : (playheadBars - cb.minBar);
        const newIds = [];
        cb.items.forEach((item) => {
          const newBar = Math.max(0, Math.min(totalBars - item.length, item.bar + offset));
          if (item.kind === "region") {
            const newId = `r${Date.now()}_${Math.random().toString(36).slice(2,7)}`;
            useDAWStore.setState((state) => ({
              regions: [...state.regions, {
                id: newId,
                trackId: item.trackId,
                bar: newBar,
                length: item.length,
                // v19.25 — Inherit source name verbatim (no "copy" suffix).
                name: item.name,
                pattern: [...(item.pattern || new Array(16).fill(false))],
                notes: (item.notes || []).map((n) => ({ ...n, id: `n${Date.now()}_${Math.random().toString(36).slice(2,5)}` })),
              }],
            }));
            newIds.push(newId);
          } else {
            const newId = `ac${Date.now()}_${Math.random().toString(36).slice(2,6)}`;
            addAudioClip(item.trackId, {
              id: newId,
              bar: newBar,
              length: item.length,
              url: item.url,
              name: item.name,
              peaks: item.peaks,
              peaksFormat: item.peaksFormat,
              duration: item.duration,
              pitch: item.pitch,
              reversed: item.reversed,
            });
            if (item.url) engine.loadAudioClip(item.trackId, newId, item.url);
            newIds.push(newId);
          }
        });
        if (newIds.length > 0) {
          setSelectedIds(newIds);
          const where = useAfterOriginal
            ? "after original"
            : `bar ${(playheadBars + 1).toFixed(2)}`;
          toast.success(`Pasted ${newIds.length} item${newIds.length > 1 ? "s" : ""} @ ${where}`);
        }
        return;
      }

      // R = reverse. Handles BOTH MIDI regions and audio clips, and
      // extends to every id in `selectedIds` so multi-selected audio
      // clips flip together in one keystroke.
      // v19.26 — audio-clip multi-select reverse + rescheduling so
      // the reversed state takes effect on the next transport tick.
      if ((e.key === "r" || e.key === "R") && !e.ctrlKey && !e.metaKey) {
        const targetIds = new Set(Array.isArray(selectedIds) && selectedIds.length ? selectedIds : (selectedRegionId ? [selectedRegionId] : []));
        if (targetIds.size === 0) return;
        e.preventDefault();
        let regionCount = 0;
        let audioCount = 0;
        for (const id of targetIds) {
          const region = regions.find((r) => r.id === id);
          if (region) {
            const newPattern = [...(region.pattern || new Array(16).fill(false))].reverse();
            const newNotes = (region.notes || []).map((n) => ({
              ...n,
              step: 16 - n.step - (n.duration || 1),
            })).filter((n) => n.step >= 0);
            updateRegion(region.id, { pattern: newPattern, notes: newNotes });
            regionCount++;
            continue;
          }
          for (const t of tracks) {
            const clip = (t.audioClips || []).find((c) => c.id === id);
            if (clip) {
              const nextReversed = !clip.reversed;
              updateAudioClip(t.id, clip.id, { reversed: nextReversed });
              // Flip the live Tone.Player buffer so the next transport
              // tick plays the reversed audio without needing a full
              // schedule rebuild.
              const player = engine.audioClipPlayers?.[t.id]?.[clip.id];
              if (player && player.buffer) { try { player.reverse = nextReversed; } catch {} }
              audioCount++;
              break;
            }
          }
        }
        // Re-run schedule so any in-flight loop picks up the new
        // reversed state on the next iteration.
        try {
          if (engine.tracksRef && Array.isArray(engine.tracksRef)) engine.scheduleAudioClips(engine.tracksRef);
        } catch { /* noop */ }
        if (regionCount + audioCount > 0) {
          const parts = [];
          if (audioCount) parts.push(`${audioCount} audio clip${audioCount > 1 ? "s" : ""}`);
          if (regionCount) parts.push(`${regionCount} region${regionCount > 1 ? "s" : ""}`);
          toast.success(`Reversed ${parts.join(" + ")}`);
        }
        return;
      }
      // Up/Down = transpose ±1 semitone (Shift+Up/Down = ±12 = octave shift).
      // v19.12 — Multi-select fix: iterate every id in `selectedIds` (which
      // includes both MIDI regions and audio clips across ANY track), not
      // just the singular `selectedRegionId`. Each item shifts by the same
      // delta and stamps its cumulative `pitchOffset` (MIDI regions) or
      // `pitch` (audio clips) so the corner badge shows the real total.
      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        const dir = e.key === "ArrowUp" ? 1 : -1;
        const delta = e.shiftKey ? 12 * dir : dir;
        // Build the target id set: everything explicitly selected via
        // marquee/click AND, as a fallback, the currently-focused single
        // region so a lone Up/Down still works.
        const ids = new Set(Array.isArray(selectedIds) ? selectedIds : []);
        if (selectedRegionId) ids.add(selectedRegionId);
        if (ids.size === 0) return;
        let hitAny = false;
        let toastMsg = null;
        for (const id of ids) {
          const region = regions.find((r) => r.id === id);
          if (region && (region.notes || []).length > 0) {
            hitAny = true;
            const newNotes = region.notes.map((n) => ({ ...n, pitch: transposeNote(n.pitch, delta) }));
            const newOffset = (region.pitchOffset || 0) + delta;
            updateRegion(region.id, { notes: newNotes, pitchOffset: newOffset });
            toastMsg = toastMsg || `Transposed ${delta > 0 ? "+" : ""}${delta} semitone${Math.abs(delta) !== 1 ? "s" : ""}`;
            continue;
          }
          for (const t of tracks) {
            const clip = (t.audioClips || []).find((c) => c.id === id);
            if (clip) {
              hitAny = true;
              const newPitch = (clip.pitch || 0) + delta;
              updateAudioClip(t.id, clip.id, { pitch: newPitch });
              // Combine any active stretch rate with the new pitch rate
              // so live playback stays consistent with the schedule.
              const player = engine.audioClipPlayers?.[t.id]?.[clip.id];
              if (player) {
                try {
                  let stretchRate = 1;
                  if (clip.stretched && clip.duration && clip.duration > 0) {
                    const secPerBar = (60 / (engine.getBpm() || 120)) * 4;
                    const durationSec = Math.max(0.01, (clip.length || 1) * secPerBar);
                    const offsetSec = Math.max(0, clip.offset || 0);
                    const naturalRemainingSec = Math.max(0.01, clip.duration - offsetSec);
                    stretchRate = Math.max(0.1, Math.min(10, naturalRemainingSec / durationSec));
                  }
                  player.playbackRate = stretchRate * Math.pow(2, newPitch / 12);
                } catch { /* noop */ }
              }
              // v19.28 — Format: "+3st" / "-1oct" (exact octave
              // multiples read as "oct" for clarity).
              const label = (newPitch !== 0 && newPitch % 12 === 0)
                ? `${newPitch > 0 ? "+" : ""}${newPitch / 12} octave${Math.abs(newPitch / 12) !== 1 ? "s" : ""}`
                : `${newPitch > 0 ? "+" : ""}${newPitch} semitone${Math.abs(newPitch) !== 1 ? "s" : ""}`;
              toastMsg = toastMsg || `Audio pitch ${label}`;
              break;
            }
          }
        }
        if (hitAny) {
          e.preventDefault();
          // Re-run the schedule so in-flight loops pick up the new
          // pitched playbackRate on the next transport tick.
          try {
            if (engine.tracksRef && Array.isArray(engine.tracksRef)) engine.scheduleAudioClips(engine.tracksRef);
          } catch { /* noop */ }
          if (toastMsg) toast.success(toastMsg);
          return;
        }
      }
      // C = toggle scissor tool, V = cursor tool
      if ((e.key === "c" || e.key === "C") && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setToolMode(toolMode === "scissor" ? "default" : "scissor");
        toast.info(toolMode === "scissor" ? "Cursor tool" : "Scissor tool");
        return;
      }
      if ((e.key === "v" || e.key === "V") && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setToolMode("default");
        return;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedRegionId, selectedIds, regions, tracks, totalBars, removeRegion, duplicateRegion, updateRegion, updateAudioClip, removeAudioClip, addAudioClip, setSelectedIds, clearSelection, snapEnabled, snapDivision, toolMode, setToolMode]);

  // Drag/drop instrument or FX onto track header
  const handleDrop = (e, track) => {
    e.preventDefault();
    setDragOverTrackId(null);
    // v19.10 — Track-reorder drop (highest priority — internal DnD).
    const reorderId = e.dataTransfer.getData("application/x-crow-track-reorder");
    if (reorderId) {
      const rect = e.currentTarget.getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      const pos = e.clientY < midY ? "before" : "after";
      reorderTracks(reorderId, track.id, pos);
      setTrackDrag(null);
      return;
    }
    const data = e.dataTransfer.getData("application/x-crow-plugin");
    if (!data) return;
    try {
      const payload = JSON.parse(data);
      if (payload.kind === "instrument") {
        assignInstrument(track.id, payload.instrumentType);
        const def = INSTRUMENT_DEFS[payload.instrumentType];
        if (def) {
          updateTrack(track.id, { name: def.label, color: def.color });
        }
        toast.success(`${payload.name} loaded onto ${track.name}`);
        if (payload.instrumentType === "synth") setActivePlugin("synth");
        if (payload.instrumentType === "sampler") {
          selectTrack(track.id);
          setActivePlugin("sampler");
        }
      } else if (payload.kind === "fx") {
        // Add FX to this track's chain
        const existing = (track.fxChain || []).length;
        if (existing >= 5) {
          toast.error("Max 5 FX per track");
          return;
        }
        addFxToTrack(track.id, payload.plugin);
        selectTrack(track.id);
        setActiveTab("plugin");
        toast.success(`Added ${payload.plugin} to ${track.name} (${existing + 1}/5)`);
      }
    } catch {}
  };

  const handleDragOver = (e, trackId) => {
    // v19.10 — Handle BOTH plugin-drop AND track-reorder over states so
    // dragging one header over another shows the drop indicator line.
    const isReorder = e.dataTransfer.types.includes("application/x-crow-track-reorder");
    const isPlugin = e.dataTransfer.types.includes("application/x-crow-plugin");
    if (!isReorder && !isPlugin) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = isReorder ? "move" : "copy";
    if (isReorder) {
      const rect = e.currentTarget.getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      const pos = e.clientY < midY ? "before" : "after";
      setTrackDrag((s) => (s && s.over === trackId && s.pos === pos ? s : { ...(s || {}), over: trackId, pos }));
    } else {
      setDragOverTrackId(trackId);
    }
  };

  // v19.10 — Handler wired onto the track header itself. Kicks off the
  // native HTML5 DnD so the browser handles the drag ghost + cursor. The
  // dragstart payload carries the source track id; drop targets read it
  // via `application/x-crow-track-reorder` mimetype.
  const handleTrackDragStart = (e, track) => {
    // Only start reorder DnD when the drag originates from the header's
    // "grab strip". Interactive children (input, buttons, color swatch)
    // opt out via stopPropagation on their own dragstart if applicable —
    // but their default type=text/color already suppresses drag.
    if (e.target.closest("input, button, [data-no-track-drag]")) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/x-crow-track-reorder", track.id);
    setTrackDrag({ id: track.id, over: null, pos: "before" });
  };

  const handleTrackDragEnd = () => setTrackDrag(null);

  const openTrackPlugin = (track) => {
    selectTrack(track.id);
    if (!track.type) return;
    if (track.type === "synth")   setActivePlugin("synth");
    else if (track.type === "sampler") setActivePlugin("sampler");
  };

  return (
    <div className="h-full flex flex-col bg-[#0e0e0e]" data-testid="arranger">
      <div className="flex items-center justify-between px-4 py-2 border-b border-[#222] gap-2">
        <div className="flex items-center gap-2">
          <h3 className="font-display font-bold text-sm">Arranger</h3>
          <span className="label-mono">{totalBars} bars · {tracks.length} tracks</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={addTrack}
            className="btn-3d px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5"
            data-testid="add-track-btn"
          >
            <Plus size={12} weight="bold" /> Add Track
          </button>
          {/* v19.15 — GUI equivalents for Cmd+D / Delete / Cmd+J so the
              same edits are one click away when producers aren't near
              the keyboard. Buttons operate on the current selection
              (`selectedIds` / `selectedRegionId`). Disabled when nothing
              is selected so the affordance is unambiguous. */}
          <div className="flex items-center gap-1 px-1.5 py-1 rounded border border-[#222] bg-[#0a0a0a]" data-testid="arranger-action-buttons">
            <button
              onClick={() => {
                const ids = (selectedIds && selectedIds.length > 0)
                  ? selectedIds
                  : (selectedRegionId ? [selectedRegionId] : []);
                if (ids.length === 0) { toast.info("Select a region or clip first"); return; }
                // Single region → overlap-aware store duplicate. Multi-
                // selection → span-shift with per-track overlap dodge.
                const items = [];
                let selStart = Infinity, selEnd = -Infinity;
                ids.forEach((id) => {
                  const region = regions.find((r) => r.id === id);
                  if (region) {
                    items.push({ kind: "region", ref: region });
                    selStart = Math.min(selStart, region.bar);
                    selEnd = Math.max(selEnd, region.bar + region.length);
                    return;
                  }
                  for (const t of tracks) {
                    const clip = (t.audioClips || []).find((c) => c.id === id);
                    if (clip) {
                      items.push({ kind: "audio", trackId: t.id, ref: clip });
                      selStart = Math.min(selStart, clip.bar);
                      selEnd = Math.max(selEnd, clip.bar + clip.length);
                      break;
                    }
                  }
                });
                if (items.length === 0) return;
                const newIds = [];
                if (items.length === 1 && items[0].kind === "region") {
                  const r = items[0].ref;
                  const beforeCount = useDAWStore.getState().regions.length;
                  useDAWStore.getState().duplicateRegion(r.id);
                  const after = useDAWStore.getState().regions;
                  if (after.length > beforeCount) newIds.push(after[after.length - 1].id);
                } else {
                  const span = Math.max(0.05, selEnd - selStart);
                  items.forEach((it) => {
                    if (it.kind === "region") {
                      const r = it.ref;
                      let newBar = Math.max(0, Math.min(totalBars - r.length, r.bar + span));
                      const sameTrack = useDAWStore.getState().regions
                        .filter((x) => x.trackId === r.trackId && !ids.includes(x.id))
                        .sort((a, b) => a.bar - b.bar);
                      for (const other of sameTrack) {
                        if (newBar + r.length <= other.bar) break;
                        if (newBar < other.bar + other.length) newBar = other.bar + other.length;
                      }
                      if (newBar + r.length > totalBars) newBar = Math.max(0, totalBars - r.length);
                      const newId = `r${Date.now()}_${Math.random().toString(36).slice(2,7)}`;
                      useDAWStore.setState((state) => ({
                        regions: [...state.regions, {
                          // v19.25 — Inherit source name verbatim (no "copy" suffix).
                          ...r, id: newId, bar: newBar,
                          pattern: [...(r.pattern || new Array(16).fill(false))],
                          notes: (r.notes || []).map((n) => ({ ...n, id: `n${Date.now()}_${Math.random().toString(36).slice(2,5)}` })),
                        }],
                      }));
                      newIds.push(newId);
                    } else {
                      const clip = it.ref;
                      let newBar = Math.max(0, Math.min(totalBars - clip.length, clip.bar + span));
                      const trk = useDAWStore.getState().tracks.find((t) => t.id === it.trackId);
                      const sameTrackClips = ((trk?.audioClips) || [])
                        .filter((c) => !ids.includes(c.id))
                        .sort((a, b) => a.bar - b.bar);
                      for (const other of sameTrackClips) {
                        if (newBar + clip.length <= other.bar) break;
                        if (newBar < other.bar + other.length) newBar = other.bar + other.length;
                      }
                      const newId = `ac${Date.now()}_${Math.random().toString(36).slice(2,6)}`;
                      addAudioClip(it.trackId, { ...clip, id: newId, bar: newBar });
                      if (clip.url) engine.loadAudioClip(it.trackId, newId, clip.url);
                      newIds.push(newId);
                    }
                  });
                }
                if (newIds.length > 0) {
                  setSelectedIds(newIds);
                  toast.success(newIds.length > 1 ? `Duplicated ${newIds.length} items` : "Duplicated");
                }
              }}
              disabled={!(selectedIds?.length || selectedRegionId)}
              className="btn-3d px-2 py-1 text-[10px] font-bold uppercase disabled:opacity-40 flex items-center gap-1"
              title="Duplicate selection (Ctrl/Cmd+D)"
              data-testid="action-duplicate-btn"
            >
              <Copy size={11} weight="bold" /> DUP
            </button>
            <button
              onClick={() => {
                const ids = (selectedIds && selectedIds.length > 0)
                  ? selectedIds
                  : (selectedRegionId ? [selectedRegionId] : []);
                if (ids.length === 0) { toast.info("Select a region or clip first"); return; }
                ids.forEach((id) => {
                  const region = regions.find((r) => r.id === id);
                  if (region) { removeRegion(id); return; }
                  for (const t of tracks) {
                    const clip = (t.audioClips || []).find((c) => c.id === id);
                    if (clip) { removeAudioClip(t.id, clip.id); return; }
                  }
                });
                clearSelection();
                toast.success(ids.length > 1 ? `Deleted ${ids.length} items` : "Deleted");
              }}
              disabled={!(selectedIds?.length || selectedRegionId)}
              className="btn-3d px-2 py-1 text-[10px] font-bold uppercase disabled:opacity-40 hover:bg-red-500/10 flex items-center gap-1"
              title="Delete selection (Delete)"
              data-testid="action-delete-btn"
            >
              <Trash size={11} weight="bold" /> DEL
            </button>
            <button
              onClick={() => {
                const ids = (selectedIds || []).filter(
                  (id) => (regions || []).some((r) => r.id === id)
                );
                if (ids.length < 2) { toast.info("Select 2+ regions to join"); return; }
                const trackIds = new Set(ids.map((id) => regions.find((r) => r.id === id)?.trackId));
                if (trackIds.size > 1) { toast.error("Selected regions must be on the same track"); return; }
                const mergedId = useDAWStore.getState().joinRegions(ids);
                if (mergedId) toast.success(`Regions joined (${ids.length} → 1)`);
              }}
              disabled={!(selectedIds && selectedIds.length >= 2)}
              className="btn-3d px-2 py-1 text-[10px] font-bold uppercase disabled:opacity-40"
              title="Join selected regions (Ctrl/Cmd+J)"
              data-testid="action-join-btn"
            >
              JOIN
            </button>
          </div>
          <div className="flex items-center gap-1 px-2 py-1 rounded border border-[#222] bg-[#0a0a0a]">
            <button
              onClick={() => setSnapEnabled(!snapEnabled)}
              className={`text-[10px] font-bold uppercase px-2 py-1 rounded ${snapEnabled ? "bg-[#3B82F6] text-white" : "text-white/40 hover:text-white"}`}
              data-testid="snap-toggle"
              title="Toggle snap to grid"
            >
              {snapEnabled ? "Snap ON" : "Free"}
            </button>
            <select
              value={snapDivision}
              onChange={(e) => setSnapDivision(+e.target.value)}
              disabled={!snapEnabled}
              className="bg-transparent text-[10px] font-mono text-white/70 focus:outline-none disabled:opacity-40"
              data-testid="snap-division-select"
              title="Snap unit"
            >
              <option value={1}>1 bar</option>
              <option value={2}>1/2</option>
              <option value={4}>1/4</option>
              <option value={8}>1/8</option>
              <option value={16}>1/16</option>
              <option value={32}>1/32</option>
            </select>
          </div>
          {selectedRegionId && (
            <button
              onClick={() => removeRegion(selectedRegionId)}
              className="btn-3d px-2 py-1.5 text-xs flex items-center gap-1"
              data-testid="delete-region-btn"
            >
              <Trash size={12} weight="bold" /> Delete Region
            </button>
          )}
          <div className="relative">
            <button
              onClick={() => setShowGridControls((s) => !s)}
              className="btn-3d px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5"
              title="Grid resize controls"
              data-testid="grid-resize-btn"
            >
              <GearSix size={12} weight="bold" /> Grid Size
            </button>
            {showGridControls && (
              <div className="absolute right-0 top-9 z-30 plugin-panel p-4 w-72" data-testid="grid-resize-popover">
                <div className="font-display font-bold text-xs mb-3">Grid Size</div>
                <div className="space-y-3">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="label-mono flex items-center gap-1"><ArrowsOutLineHorizontal size={11} weight="bold" /> Horizontal · {pixelsPerBar}px/bar</span>
                      <span className="text-[10px] font-mono text-white/50">{pixelsPerBar}</span>
                    </div>
                    <input type="range" min="40" max="500" step="10" value={pixelsPerBar}
                      onChange={(e) => setPixelsPerBar(+e.target.value)}
                      className="w-full accent-blue-500"
                      data-testid="grid-horizontal-slider" />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="label-mono flex items-center gap-1"><ArrowsOutLineVertical size={11} weight="bold" /> Vertical · {trackHeight}px/track</span>
                      <span className="text-[10px] font-mono text-white/50">{trackHeight}</span>
                    </div>
                    <input type="range" min="36" max="140" step="2" value={trackHeight}
                      onChange={(e) => setTrackHeight(+e.target.value)}
                      className="w-full accent-blue-500"
                      data-testid="grid-vertical-slider" />
                  </div>
                  <div className="flex gap-2 pt-2 border-t border-[#262626]">
                    <button onClick={() => { setPixelsPerBar(180); setTrackHeight(56); }}
                      className="btn-3d flex-1 py-1.5 text-[11px]" data-testid="grid-reset-btn">Reset</button>
                    <button
                      onClick={() => {
                        // Fit entire timeline + all tracks into the visible viewport
                        const wrapper = document.querySelector('[data-testid="arranger"]');
                        if (!wrapper) return;
                        const w = wrapper.clientWidth - 210 - 24;
                        const h = wrapper.clientHeight - 110;
                        setPixelsPerBar(Math.max(40, Math.floor(w / totalBars)));
                        const visibleTracks = Math.max(1, tracks.length);
                        setTrackHeight(Math.max(36, Math.floor(h / visibleTracks)));
                      }}
                      className="btn-3d flex-1 py-1.5 text-[11px]" data-testid="grid-fit-btn">Fit</button>
                    <button onClick={() => setShowGridControls(false)}
                      className="btn-3d-accent btn-3d flex-1 py-1.5 text-[11px] font-bold">Done</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Loop strip - double-click to create/clear loop region.
          v19.22 — Wrapped in `loopStripScrollRef` (overflow-hidden but
          programmatically scrollable) and mirrored to the tracks
          container's scrollLeft in the same onScroll handler that syncs
          the timeline ruler. Before this, scrolling right left the
          LoopBar rendered at its absolute left offset while the tracks
          slid underneath — the loop marker visibly drifted relative to
          the ruler. Now all three horizontal strips (loop / ruler /
          tracks) move as one unit. */}
      <div ref={loopStripScrollRef} className="flex h-5 border-b border-[#222] bg-[#080808] overflow-hidden">
        <div className="w-[210px] flex-shrink-0 border-r border-[#222] flex items-center px-2 label-mono text-[8px] sticky left-0 z-10 bg-[#080808]">LOOP</div>
        <div
          className="flex relative cursor-crosshair"
          style={{ width: totalBars * pixelsPerBar }}
          onDoubleClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const startBar = Math.max(0, Math.floor(x / pixelsPerBar));
            // If loop exists and click is inside it, clear; else create 2-bar loop at click
            if (loop && startBar >= loop.start && startBar < loop.end) {
              setLoop(null);
              toast.info("Loop cleared");
            } else {
              const len = 2;
              const end = Math.min(totalBars, startBar + len);
              setLoop({ start: startBar, end });
              toast.success(`Loop region: bars ${startBar + 1}–${end}`);
            }
          }}
          data-testid="loop-strip"
        >
          {Array.from({ length: totalBars }, (_, i) => (
            <div key={i} className="border-l border-[#1a1a1a]" style={{ width: pixelsPerBar }} />
          ))}
          {loop && (
            <LoopBar
              loop={loop}
              setLoop={setLoop}
              totalBars={totalBars}
              pixelsPerBar={pixelsPerBar}
            />
          )}
        </div>
      </div>

      {/* Timeline ruler — the outer div is horizontally scrollable and its
          scrollLeft is 100% mirrored to the tracks container below via the
          onScroll handler on tracksScrollRef. Without this, scrolling the
          arranger from Bar 1 to Bar 28 leaves the bar labels static and the
          markers misalign with the regions.
          v10.1 — click ANYWHERE on the ruler to move the playhead to that
          bar. Playback then starts from the clicked position (standard
          DAW behaviour). */}
      <div ref={timelineScrollRef} className="flex h-7 border-b border-[#222] bg-[#0a0a0a] overflow-hidden">
        <div className="w-[210px] flex-shrink-0 border-r border-[#222] sticky left-0 z-10 bg-[#0a0a0a]" />
        <div
          className="flex relative cursor-pointer"
          style={{ width: totalBars * pixelsPerBar }}
          data-testid="arranger-timeline-bars"
          onClick={(e) => {
            // Compute the bar position from the mouse X relative to the
            // ruler track (BARS start after the 180px sticky sidebar,
            // but this inner div's own origin is already at bar 1, so we
            // just read offsetX). Clamp so clicks past the last bar
            // land ON the last bar rather than past it.
            const rect = e.currentTarget.getBoundingClientRect();
            const barFloat = Math.max(0, Math.min(totalBars - 0.0001, (e.clientX - rect.left) / pixelsPerBar));
            try {
              Tone.getTransport().position = `${Math.floor(barFloat)}:${Math.floor((barFloat % 1) * 4)}:0`;
            } catch { /* transport not ready yet — ignore */ }
          }}
        >
          {Array.from({ length: totalBars }, (_, i) => (
            <div key={i} className="border-l border-[#222] flex items-center px-2 label-mono" style={{ width: pixelsPerBar }}>
              BAR {i + 1}
            </div>
          ))}
        </div>
      </div>

      {/* Tracks */}
      <div
        className="flex-1 overflow-auto relative"
        ref={tracksScrollRef}
        data-testid="arranger-tracks-scroll"
        onScroll={(e) => {
          // v19.22 — Mirror scrollLeft to BOTH the top timeline ruler AND
          // the loop strip so all three horizontal bands move as one
          // unit. Missing the loop strip caused the loop marker to drift
          // relative to the bar ruler at any non-zero scroll offset.
          const sl = e.currentTarget.scrollLeft;
          if (timelineScrollRef.current) timelineScrollRef.current.scrollLeft = sl;
          if (loopStripScrollRef.current) loopStripScrollRef.current.scrollLeft = sl;
        }}
        style={toolMode === "scissor" ? { cursor: "crosshair" } : undefined}
        onMouseDown={(e) => {
          // Only left button
          if (e.button !== 0) return;
          if (!tracksScrollRef.current) return;
          // Skip if clicked on an interactive element
          const t = e.target;
          if (t.closest('[data-testid^="audio-clip-"]') ||
              t.closest('[data-testid^="region-"]') ||
              t.closest('[data-testid^="track-header-"]') ||
              t.closest('button') ||
              t.closest('input') ||
              t.closest('select') ||
              t.closest('[data-resize]')) {
            return;
          }
          const container = tracksScrollRef.current;
          const rect = container.getBoundingClientRect();
          const x0 = e.clientX - rect.left + container.scrollLeft;
          const y0 = e.clientY - rect.top + container.scrollTop;
          marqueeRef.current = { x1: x0, y1: y0, x2: x0, y2: y0 };
          setMarquee(marqueeRef.current);
          e.preventDefault(); // prevent text selection during marquee drag

          const onMove = (ev) => {
            const x = ev.clientX - rect.left + container.scrollLeft;
            const y = ev.clientY - rect.top + container.scrollTop;
            marqueeRef.current = { ...marqueeRef.current, x2: x, y2: y };
            setMarquee(marqueeRef.current);
          };
          const onUp = () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            const m = marqueeRef.current;
            marqueeRef.current = null;
            setMarquee(null);
            if (!m) return;
            const minX = Math.min(m.x1, m.x2);
            const maxX = Math.max(m.x1, m.x2);
            const minY = Math.min(m.y1, m.y2);
            const maxY = Math.max(m.y1, m.y2);
            // Tiny drag = treat as click on empty area -> clear selection
            if (maxX - minX < 4 && maxY - minY < 4) {
              clearSelection();
              return;
            }
            const HEADER_W = 210;
            const ids = [];
            tracks.forEach((track, idx) => {
              const trackTop = idx * trackHeight;
              const trackBottom = trackTop + trackHeight;
              if (maxY < trackTop || minY > trackBottom) return;
              if (track.type === "audio") {
                (track.audioClips || []).forEach((clip) => {
                  const cx1 = HEADER_W + clip.bar * pixelsPerBar;
                  const cx2 = HEADER_W + (clip.bar + clip.length) * pixelsPerBar;
                  if (cx2 < minX || cx1 > maxX) return;
                  ids.push(clip.id);
                });
              } else {
                regions.filter((r) => r.trackId === track.id).forEach((r) => {
                  const rx1 = HEADER_W + r.bar * pixelsPerBar;
                  const rx2 = HEADER_W + (r.bar + r.length) * pixelsPerBar;
                  if (rx2 < minX || rx1 > maxX) return;
                  ids.push(r.id);
                });
              }
            });
            setSelectedIds(ids);
            if (ids.length > 0) toast.success(`Selected ${ids.length} item${ids.length > 1 ? "s" : ""}`);
          };
          window.addEventListener("mousemove", onMove);
          window.addEventListener("mouseup", onUp);
        }}
      >
        {tracks.length === 0 && (
          <div className="text-center py-10 text-white/40 text-sm">
            No tracks yet. Click <span className="text-white/80">+ Add Track</span> to begin.
          </div>
        )}
        {tracks.map((track) => {
          const isSel = selectedTrackId === track.id;
          const isHot = dragOverTrackId === track.id;
          // v11.3 — Blocked drop indicator (region dragged from an
          // incompatible source e.g. piano melody over a kick lane).
          const isBlocked = dragOverTrackId === `__block_${track.id}`;
          return (
            <div
              key={track.id}
              className={`track-row flex relative ${isSel ? "bg-[#181818]" : ""} ${isBlocked ? "bg-red-500/10" : ""}`}
              style={{ height: trackHeight }}
              onClick={() => selectTrack(track.id)}
            >
              {/* v17.4 — Track Header: 2-ROW VERTICAL LAYOUT.
                  Row 1: color swatch + full-width track name input (max typing space,
                          text no longer gets clipped by control buttons).
                  Row 2: instrument label + compact icon-only control cluster.
                  Widened to 210px to give producer more room for long track names
                  like "Lead Vocal (Verse 2)" without truncation. */}
              <div
                className={`w-[210px] flex-shrink-0 flex flex-col justify-center gap-1 px-2 py-1.5 border-r border-[#222] transition cursor-grab active:cursor-grabbing sticky left-0 z-10 ${isHot ? "ring-2 ring-inset ring-[#00FF41]" : ""} ${trackDrag?.id === track.id ? "opacity-50" : ""}`}
                style={{
                  borderLeft: `3px solid ${track.color}`,
                  background: isHot
                    ? "linear-gradient(180deg, #00FF4118, #1a1a1a)"
                    : (isSel ? "linear-gradient(180deg, #2a2a2a, #1d1d1d)" : "linear-gradient(180deg, #232323, #1a1a1a)"),
                }}
                draggable
                onDragStart={(e) => handleTrackDragStart(e, track)}
                onDragEnd={handleTrackDragEnd}
                onDragOver={(e) => handleDragOver(e, track.id)}
                onDragLeave={() => setDragOverTrackId(null)}
                onDrop={(e) => handleDrop(e, track)}
                onDoubleClick={() => openTrackPlugin(track)}
                data-testid={`track-header-${track.id}`}
              >
                {/* v19.10 — Drop-position indicator. Rendered as a 2-px
                    high full-width green bar at the top or bottom edge
                    of the target header while a reorder drag is over it. */}
                {trackDrag?.over === track.id && trackDrag.id !== track.id && (
                  <div
                    className="absolute left-0 right-0 h-[2px] bg-[#00FF41] pointer-events-none"
                    style={{ top: trackDrag.pos === "before" ? -1 : "auto", bottom: trackDrag.pos === "after" ? -1 : "auto", boxShadow: "0 0 6px #00FF41" }}
                    data-testid={`track-drop-indicator-${track.id}`}
                  />
                )}
                {/* Row 1 — Color swatch + full-width name input */}
                <div className="flex items-center gap-1.5 w-full">
                  <label
                    className="w-3 h-3 rounded-full flex-shrink-0 cursor-pointer relative"
                    style={{ background: track.color, boxShadow: `0 0 6px ${track.color}` }}
                    onClick={(e) => e.stopPropagation()}
                    title="Click to change track color — cascades to region + notes"
                    data-testid={`track-color-swatch-${track.id}`}
                  >
                    <input
                      type="color"
                      value={track.color || "#22C55E"}
                      onChange={(e) => setTrackColor(track.id, e.target.value)}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      data-testid={`track-color-input-${track.id}`}
                    />
                  </label>
                  <input
                    type="text"
                    value={track.name}
                    onChange={(e) => updateTrack(track.id, { name: e.target.value })}
                    onClick={(e) => e.stopPropagation()}
                    className="flex-1 min-w-0 bg-transparent text-[12px] leading-tight font-semibold w-full focus:outline-none focus:bg-[#0a0a0a] hover:bg-[#0a0a0a]/40 rounded px-1.5 py-1 border border-transparent focus:border-[#00FF41]/40 transition-colors"
                    data-testid={`track-name-${track.id}`}
                  />
                </div>
                {/* Row 2 — Instrument label + compact icon-only controls */}
                <div className="flex items-center gap-1 w-full pl-[18px]">
                  <div className="text-[9px] font-mono text-white/40 flex items-center gap-1 flex-1 min-w-0 truncate">
                    <span className="truncate">{track.type ? INSTRUMENT_DEFS[track.type]?.label : "drag instrument →"}</span>
                    {(track.fxChain || []).length > 0 && (
                      <span
                        className="px-1 rounded bg-[#3B82F6]/20 border border-[#3B82F6]/40 text-[#3B82F6] font-bold cursor-pointer flex-shrink-0"
                        onClick={(e) => { e.stopPropagation(); selectTrack(track.id); setActiveTab("plugin"); }}
                        data-testid={`fx-count-${track.id}`}
                      >
                        FX×{(track.fxChain || []).length}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-0.5 flex-shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleMute(track.id); }}
                      className={`text-[9px] font-mono rounded w-[16px] h-[16px] flex items-center justify-center ${track.muted ? "bg-red-500 text-white" : "text-white/40 border border-[#222] hover:text-white/80"}`}
                      title="Mute (M)"
                      data-testid={`mute-${track.id}`}
                    >M</button>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleSolo(track.id); }}
                      className={`text-[9px] font-mono rounded w-[16px] h-[16px] flex items-center justify-center ${track.solo ? "bg-yellow-500 text-black" : "text-white/40 border border-[#222] hover:text-white/80"}`}
                      title="Solo (S)"
                      data-testid={`solo-${track.id}`}
                    >S</button>
                    {/* v13.1 — Volume Automation toggle. */}
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleTrackAutomation(track.id); }}
                      className={`text-[9px] font-mono rounded w-[16px] h-[16px] flex items-center justify-center ${track.automation?.volume?.enabled ? "bg-emerald-500 text-black" : "text-white/40 border border-[#222] hover:text-white/80"}`}
                      title="Toggle volume automation (A)"
                      data-testid={`automation-toggle-${track.id}`}
                    >A</button>
                    {/* v14.1 — Record Arm (audio tracks only). */}
                    {track.type === "audio" && (
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleRecordArm(track.id); }}
                        className={`text-[9px] font-mono rounded w-[16px] h-[16px] flex items-center justify-center ${track.recordArmed ? "bg-red-500 text-white animate-pulse" : "text-white/40 border border-[#222] hover:text-white/80"}`}
                        title="Arm this track for mic recording (R)"
                        data-testid={`record-arm-${track.id}`}
                      >R</button>
                    )}
                    {/* v11.3 — Duplicate track. */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        duplicateTrack(track.id);
                        toast.success(`Duplicated "${track.name}"`);
                      }}
                      className="text-[9px] text-white/40 hover:text-cyan-300 border border-[#222] rounded w-[16px] h-[16px] flex items-center justify-center"
                      title="Duplicate track (with FX + all regions)"
                      data-testid={`duplicate-track-${track.id}`}
                    ><Copy size={10} weight="bold" /></button>
                    <button
                      onClick={(e) => { e.stopPropagation(); removeTrack(track.id); }}
                      className="text-[9px] text-white/30 hover:text-red-400 w-[16px] h-[16px] flex items-center justify-center"
                      title="Remove track"
                      data-testid={`remove-track-${track.id}`}
                    ><X size={10} weight="bold" /></button>
                  </div>
                </div>
              </div>

              <div
                className="flex relative"
                onDragOver={(e) => {
                  if (track.type === "audio" && e.dataTransfer.types.includes("Files")) {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "copy";
                  }
                }}
                onDrop={async (e) => {
                  if (track.type !== "audio") return;
                  const file = e.dataTransfer.files?.[0];
                  if (!file) return;
                  e.preventDefault();
                  // v19.20 — Accept audio AND video files. `decodeAudioData`
                  // in modern browsers pulls the audio track straight out
                  // of an MP4 / WebM / MOV / MKV / AVI container, so a
                  // "drag your podcast/vlog and get an audio clip" flow
                  // needs no external tools. If the container has no
                  // audio track (or the codec isn't supported natively),
                  // we surface a clear toast so the user knows to convert
                  // manually — but the vast majority of consumer video
                  // files (H.264+AAC in MP4, VP8/VP9+Opus in WebM) work.
                  const isAudio = file.type.startsWith("audio/") || /\.(wav|mp3|ogg|aac|m4a|flac)$/i.test(file.name);
                  const isVideo = file.type.startsWith("video/") || /\.(mp4|mov|webm|mkv|avi|m4v)$/i.test(file.name);
                  if (!isAudio && !isVideo) {
                    toast.error("Drop an audio or video file (.wav, .mp3, .mp4, .mov, .webm, …)");
                    return;
                  }
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  let bar = Math.max(0, x / pixelsPerBar);
                  if (snapEnabled) bar = Math.round(bar / (1 / Math.max(1, snapDivision))) * (1 / Math.max(1, snapDivision));
                  const url = URL.createObjectURL(file);
                  const label = isVideo ? "Extracting audio from video…" : "Loading audio…";
                  const toastId = toast.loading(label);
                  try {
                    const { peaks, peaksFormat, duration, audioUrl } = await decodeAudioFile(file);
                    // v19.20 — For videos, `decodeAudioFile` returns a
                    // pure-WAV blob URL (`audioUrl`) — Tone.Player then
                    // never has to decode a video container. For plain
                    // audio drops, keep the original object URL.
                    const clipUrl = audioUrl || url;
                    const secPerBar = (60 / engine.bpmRef) * 4;
                    const length = Math.max(0.25, Math.round((duration / secPerBar) * 4) / 4);
                    const clipId = `ac${Date.now()}_${Math.random().toString(36).slice(2,6)}`;
                    // Push to engine first, then to store
                    await engine.loadAudioClip(track.id, clipId, clipUrl);
                    // Strip the extension off the display name and add a
                    // small "(from video)" suffix so the producer can see
                    // at a glance which clips came from a video import.
                    const cleanName = file.name.replace(/\.[^.]+$/, "");
                    const displayName = isVideo ? `${cleanName} (audio)` : file.name;
                    addAudioClip(track.id, { id: clipId, bar, length, url: clipUrl, name: displayName, peaks, peaksFormat, duration });
                    toast.success(
                      isVideo
                        ? `Extracted audio from ${file.name} at bar ${(bar + 1).toFixed(2)}`
                        : `Loaded ${file.name} at bar ${(bar + 1).toFixed(2)}`,
                      { id: toastId }
                    );
                  } catch (err) {
                    // Most common failure mode for video: the audio codec
                    // in the container isn't supported by the browser's
                    // WebCodecs (e.g. AC3 audio inside an .mkv). Give the
                    // user actionable guidance instead of a raw error.
                    const msg = isVideo
                      ? `Couldn't extract audio — the video's audio codec isn't supported. Try re-encoding to MP4 (AAC) first.`
                      : `Failed to decode audio: ${err.message}`;
                    toast.error(msg, { id: toastId, duration: 6000 });
                  }
                }}
                data-testid={`track-grid-${track.id}`}
                // v14.0 — GRID-STRETCH LAG FIX. The per-bar sub-division
                // ticks (quarter/16th/64th grid lines) used to be rendered
                // as thousands of individual absolutely-positioned <div>
                // elements per track (96 bars × up to 64 subdivs × N
                // tracks = 30k+ DOM nodes at max zoom), which starved the
                // main thread and stuttered Web Audio callbacks. Now they
                // are drawn purely as a stacked repeating-linear-gradient
                // background on the outer track grid — zero DOM cost.
                style={{
                  width: totalBars * pixelsPerBar,
                  // v19.22 — Snap the divided gridline widths to whole
                  // integers before injecting them into the gradient
                  // strings. Fractional widths like 70/4=17.5 or
                  // 180/16=11.25 caused the browser to rasterize the
                  // 1px accent line at inconsistent subpixel positions
                  // across the width of the grid — some cells rendered
                  // the line, others dropped it, producing the "gaps"
                  // the user reported when scrolling.
                  backgroundImage: [
                    // Bar lines (every bar).
                    `repeating-linear-gradient(90deg, transparent 0, transparent ${Math.round(pixelsPerBar) - 1}px, rgba(255,255,255,0.09) ${Math.round(pixelsPerBar) - 1}px, rgba(255,255,255,0.09) ${Math.round(pixelsPerBar)}px)`,
                    // Quarter-note lines (shown when zoomed in enough).
                    pixelsPerBar >= 60
                      ? `repeating-linear-gradient(90deg, transparent 0, transparent ${Math.round(pixelsPerBar / 4) - 1}px, rgba(255,255,255,0.06) ${Math.round(pixelsPerBar / 4) - 1}px, rgba(255,255,255,0.06) ${Math.round(pixelsPerBar / 4)}px)`
                      : "",
                    // 1/16 note lines (shown when zoomed in a lot).
                    pixelsPerBar >= 140
                      ? `repeating-linear-gradient(90deg, transparent 0, transparent ${Math.round(pixelsPerBar / 16) - 1}px, rgba(255,255,255,0.03) ${Math.round(pixelsPerBar / 16) - 1}px, rgba(255,255,255,0.03) ${Math.round(pixelsPerBar / 16)}px)`
                      : "",
                  ].filter(Boolean).join(","),
                }}
                onDoubleClick={(e) => {
                  if (e.target !== e.currentTarget) return;
                  if (track.type === "audio") return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  const bar = Math.max(0, Math.floor((e.clientX - rect.left) / pixelsPerBar));
                  addRegion(track.id, bar, 1);
                }}
              >
                {/* v14.0 — subdivision <div>s deleted; drawn by CSS above. */}

                {/* Audio clips on audio tracks */}
                {track.type === "audio" && (track.audioClips || []).map((clip) => {
                  const isSelected = selectedIds.includes(clip.id);
                  return (
                    <div
                      key={clip.id}
                      className="absolute top-1.5 bottom-1.5 cursor-grab active:cursor-grabbing flex flex-col text-[10px] font-semibold overflow-hidden rounded-lg border"
                      style={{
                        left: clip.bar * pixelsPerBar + 2,
                        width: clip.length * pixelsPerBar - 4,
                        background: `linear-gradient(180deg, ${track.color}D0, ${track.color}90)`,
                        borderColor: isSelected ? "#fff" : track.color,
                        boxShadow: isSelected
                          ? `inset 0 1px 0 rgba(255,255,255,0.4), 0 0 0 2px rgba(255,255,255,0.4), 0 2px 8px ${track.color}80`
                          : `inset 0 1px 0 rgba(255,255,255,0.25), 0 2px 6px ${track.color}60`,
                        color: "#fff",
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        // Scissor tool: click → split at the cut position
                        if (toolMode === "scissor") {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const offsetX = e.clientX - rect.left;
                          let atBar = clip.bar + offsetX / pixelsPerBar;
                          if (snapEnabled) {
                            const snapStep = 1 / Math.max(1, snapDivision);
                            atBar = Math.round(atBar / snapStep) * snapStep;
                          }
                          splitAudioClip(track, clip, atBar);
                          return;
                        }
                        selectRegion(clip.id);
                        const startX = e.clientX;
                        const startBar = clip.bar;
                        const resizeSide = e.target?.dataset?.resize; // "left" | "right" | "stretch" | undefined
                        const startLength = clip.length;
                        const startOffset = clip.offset || 0;
                        const secPerBar = (60 / engine.getBpm()) * 4;
                        // v19.24 — Stretch mode. Triggered by:
                        //   • Dedicated stretch-handle (data-resize="stretch"), OR
                        //   • Alt/Option held on the right-edge resize handle
                        // Both edit the clip's on-timeline length; the engine
                        // adjusts playbackRate so the whole audio content maps
                        // into the new duration (no gap, no cutoff).
                        const isStretchMode = resizeSide === "stretch" || (resizeSide === "right" && (e.altKey || e.metaKey));
                        // Audio clips: FREE placement by default. Only snap if user enabled it.
                        const useSnap = snapEnabled;
                        const snapStep = 1 / Math.max(1, snapDivision);
                        const naturalLenBars = clip.duration ? (clip.duration / secPerBar) : startLength;
                        // Show a live-ratio badge while stretching so the
                        // producer sees exactly what they're doing.
                        if (isStretchMode) {
                          setStretchTooltip({ clipId: clip.id, ratio: (startLength / naturalLenBars) });
                        }
                        const onMove = (ev) => {
                          const dx = ev.clientX - startX;
                          const dBars = dx / pixelsPerBar;
                          if (isStretchMode) {
                            let newLen = startLength + dBars;
                            newLen = useSnap ? Math.max(snapStep, Math.round(newLen / snapStep) * snapStep) : Math.max(0.05, newLen);
                            // Clamp to a sane stretch range so the producer
                            // can't accidentally slow a clip to 100× and
                            // freeze the audio graph. 0.25×…4× matches every
                            // major DAW's default limits.
                            const minLen = naturalLenBars * 0.25;
                            const maxLen = naturalLenBars * 4;
                            newLen = Math.max(minLen, Math.min(maxLen, newLen));
                            if (clip.bar + newLen <= totalBars) {
                              updateAudioClip(track.id, clip.id, { length: newLen, stretched: true });
                              setStretchTooltip({ clipId: clip.id, ratio: (newLen / naturalLenBars) });
                            }
                          } else if (resizeSide === "right") {
                            // Trim/extend the TAIL of the clip (no time-stretch)
                            let newLen = startLength + dBars;
                            newLen = useSnap ? Math.max(snapStep, Math.round(newLen / snapStep) * snapStep) : Math.max(0.05, newLen);
                            if (clip.bar + newLen <= totalBars) updateAudioClip(track.id, clip.id, { length: newLen });
                          } else if (resizeSide === "left") {
                            // Trim the HEAD of the clip — mirror bar & offset so audio content stays in place
                            let dB = dBars;
                            const maxLeftBars = startOffset / secPerBar;
                            dB = Math.max(-maxLeftBars, dB);
                            dB = Math.min(startLength - 0.05, dB);
                            if (useSnap) dB = Math.round(dB / snapStep) * snapStep;
                            const newBar = Math.max(0, startBar + dB);
                            const newLength = Math.max(0.05, startLength - dB);
                            const newOffset = Math.max(0, startOffset + dB * secPerBar);
                            updateAudioClip(track.id, clip.id, { bar: newBar, length: newLength, offset: newOffset });
                          } else {
                            let newBar = startBar + dBars;
                            newBar = useSnap ? Math.round(newBar / snapStep) * snapStep : newBar;
                            newBar = Math.max(0, Math.min(totalBars - clip.length, newBar));
                            updateAudioClip(track.id, clip.id, { bar: newBar });
                          }
                        };
                        const onUp = () => {
                          if (isStretchMode) setStretchTooltip(null);
                          window.removeEventListener("mousemove", onMove);
                          window.removeEventListener("mouseup", onUp);
                        };
                        window.addEventListener("mousemove", onMove);
                        window.addEventListener("mouseup", onUp);
                      }}
                      data-testid={`audio-clip-${clip.id}`}
                    >
                      <div className="px-2 py-0.5 truncate text-[9px] font-mono bg-black/30">{clip.name}</div>
                      {/* v19.12 — Audio-clip semitone badge.
                          v19.28 — Formatted display (+3st / -1oct) and
                          double-click resets pitch back to 0. */}
                      {clip.pitch ? (() => {
                        const p = clip.pitch;
                        const sign = p > 0 ? "+" : "";
                        // Exact octave multiples render as "+1oct" /
                        // "-2oct" so producers instantly see they've
                        // jumped a whole octave rather than reading a
                        // "12".
                        const label = (p % 12 === 0)
                          ? `${sign}${p / 12}oct`
                          : `${sign}${p}st`;
                        return (
                          <div
                            className="absolute top-1 left-1 px-1.5 py-0.5 rounded-sm bg-black/50 text-[9px] font-mono font-bold cursor-pointer hover:bg-black/70"
                            data-testid={`clip-pitch-badge-${clip.id}`}
                            title="Double-click to reset pitch to 0"
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              updateAudioClip(track.id, clip.id, { pitch: 0 });
                              const player = engine.audioClipPlayers?.[track.id]?.[clip.id];
                              if (player) { try { player.playbackRate = 1; } catch { /* noop */ } }
                              try {
                                if (engine.tracksRef && Array.isArray(engine.tracksRef)) engine.scheduleAudioClips(engine.tracksRef);
                              } catch { /* noop */ }
                              toast.success("Pitch reset to 0");
                            }}
                          >
                            {label}
                          </div>
                        );
                      })() : null}
                      {/* v19.24 — Persistent stretch-ratio badge (shown
                          only when the clip is NOT at 1.0× so an
                          untouched clip stays visually clean). */}
                      {clip.stretched && clip.duration ? (() => {
                        const secPerBar2 = (60 / (engine.getBpm() || 120)) * 4;
                        const naturalLenBars2 = clip.duration / secPerBar2;
                        const r = clip.length / naturalLenBars2;
                        if (Math.abs(r - 1) < 0.02) return null;
                        return (
                          <div
                            className="absolute top-1 right-6 px-1.5 py-0.5 rounded-sm bg-cyan-500/40 border border-cyan-300/60 text-[9px] font-mono font-bold pointer-events-none text-white"
                            data-testid={`clip-stretch-badge-${clip.id}`}
                            title="Time-stretched clip · double-click stretch handle to reset"
                          >{r.toFixed(2)}×</div>
                        );
                      })() : null}
                      {/* Live ratio tooltip during Alt-drag. */}
                      {stretchTooltip?.clipId === clip.id ? (
                        <div className="absolute top-6 left-1/2 -translate-x-1/2 px-2 py-1 rounded bg-black/85 border border-cyan-300/60 text-cyan-200 text-[11px] font-mono font-bold pointer-events-none z-30" data-testid="stretch-tooltip">
                          {stretchTooltip.ratio.toFixed(2)}× {stretchTooltip.ratio > 1 ? "(slower)" : stretchTooltip.ratio < 1 ? "(faster)" : ""}
                        </div>
                      ) : null}
                      <div className="flex-1 min-h-0 relative">
                        <Waveform peaks={clip.peaks} peaksFormat={clip.peaksFormat} offset={clip.offset || 0} duration={clip.duration} lengthSec={(clip.length || 1) * (60 / Math.max(1, engine.getBpm() || 120)) * 4} stretched={!!clip.stretched} reversed={!!clip.reversed} />
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); engine.disposeAudioClip(track.id, clip.id); removeAudioClip(track.id, clip.id); }}
                        className="absolute top-1 right-1 text-white/70 hover:text-white"
                        title="Remove clip"
                      ><X size={10} weight="bold" /></button>
                      <div
                        data-resize="left"
                        className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/30"
                        style={{ borderTopLeftRadius: 8, borderBottomLeftRadius: 8 }}
                        title="Drag to trim the start"
                      />
                      <div
                        data-resize="right"
                        className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/30"
                        style={{ borderTopRightRadius: 8, borderBottomRightRadius: 8 }}
                        title="Drag to trim the end · Alt/Option-drag to time-stretch"
                      />
                      {/* v19.24 — Dedicated stretch handle. Sits just
                          inside the trim handle on the right edge, only
                          appears when the clip is selected. Double-click
                          resets stretch to 1.0×. */}
                      {isSelected ? (
                        <div
                          data-resize="stretch"
                          data-testid={`clip-stretch-handle-${clip.id}`}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            const secPerBar3 = (60 / (engine.getBpm() || 120)) * 4;
                            const naturalLenBars3 = clip.duration ? clip.duration / secPerBar3 : clip.length;
                            updateAudioClip(track.id, clip.id, { length: naturalLenBars3, stretched: false });
                            toast.success("Stretch reset to 1.00×");
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-6 flex items-center justify-center cursor-ew-resize"
                          style={{
                            background: "linear-gradient(180deg, rgba(6,182,212,0.5), rgba(8,145,178,0.4))",
                            border: "1px solid rgba(103,232,249,0.7)",
                            borderRadius: 2,
                            boxShadow: "0 0 6px rgba(6,182,212,0.55)",
                          }}
                          title="Alt-drag to time-stretch · double-click to reset to 1.00×"
                        >
                          <span className="text-[9px] font-mono font-black text-white select-none pointer-events-none leading-none">⇔</span>
                        </div>
                      ) : null}
                    </div>
                  );
                })}

                {/* MIDI regions on non-audio tracks */}
                {track.type !== "audio" && regions.filter((r) => r.trackId === track.id).map((region) => {
                  const isSelected = selectedIds.includes(region.id);
                  return (
                    <div
                      key={region.id}
                      className="region-bubble absolute top-1.5 bottom-1.5 cursor-grab active:cursor-grabbing flex flex-col text-[11px] font-semibold overflow-hidden"
                      style={{
                        left: region.bar * pixelsPerBar + 2,
                        width: region.length * pixelsPerBar - 4,
                        background: `linear-gradient(180deg, ${track.color}E6, ${track.color}AA)`,
                        borderColor: isSelected ? "#fff" : track.color,
                        boxShadow: isSelected
                          ? `inset 0 1px 0 rgba(255,255,255,0.4), 0 0 0 2px rgba(255,255,255,0.4), 0 2px 8px ${track.color}80`
                          : `inset 0 1px 0 rgba(255,255,255,0.25), 0 2px 6px ${track.color}60`,
                        color: "#fff", textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                      }}
                      onMouseDown={(e) => handleRegionMouseDown(e, region)}
                      onClick={(e) => { e.stopPropagation(); selectRegion(region.id); }}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        selectRegion(region.id);
                        const trk = tracks.find((t) => t.id === region.trackId);
                        if (trk && (trk.type === "synth" || trk.type === "sampler")) setActiveTab("piano");
                        else setActiveTab("sequencer");
                      }}
                      data-testid={`region-${region.id}`}
                    >
                      <div className="flex items-center px-2 py-0.5">
                        <span className="truncate flex-1">{region.name}</span>
                      </div>
                      {/* v19.12 — Cumulative semitone-offset badge. Only
                          shows when the region has been transposed at
                          least once (pitchOffset ≠ 0). Sits inside the
                          MIDI bubble (not the Piano Roll) per user spec. */}
                      {region.pitchOffset ? (
                        <div
                          className="absolute top-1 right-1 px-1.5 py-0.5 rounded-sm bg-black/50 text-[9px] font-mono font-bold pointer-events-none"
                          data-testid={`region-pitch-badge-${region.id}`}
                        >
                          {region.pitchOffset > 0 ? "+" : ""}{region.pitchOffset}
                        </div>
                      ) : null}
                      <div className="flex-1 px-1 pb-1 relative min-h-0">
                        <GhostNotes track={track} region={region} />
                      </div>
                      <div data-resize="true" className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/30" style={{ borderTopRightRadius: 10, borderBottomRightRadius: 10 }} />
                    </div>
                  );
                })}

                {/* Plus button to quick-add region on this track */}
                <button
                  onClick={() => handleAddRegion(track.id)}
                  className="absolute top-1.5 right-1 w-6 h-6 rounded-md border border-dashed border-white/20 text-white/40 hover:text-white hover:border-white/60 flex items-center justify-center"
                  title="Add region"
                  data-testid={`add-region-${track.id}`}
                ><Plus size={11} weight="bold" /></button>

                {/* v13.1 — Volume Automation lane overlay. Renders only
                    when this track has automation.volume.enabled=true.
                    The horizontal 0dB line + connecting node polyline
                    live in an SVG that spans the full track lane width. */}
                {track.automation?.volume?.enabled && (
                  <AutomationLane
                    track={track}
                    pixelsPerBar={pixelsPerBar}
                    totalBars={totalBars}
                    trackHeight={trackHeight}
                    onAddNode={(bar, value) => addAutomationNode(track.id, bar, value)}
                    onUpdateNode={(idx, patch) => updateAutomationNode(track.id, idx, patch)}
                    onRemoveNode={(idx) => removeAutomationNode(track.id, idx)}
                  />
                )}

                {/* v19.15 — Playhead line is ALWAYS visible. Previously
                    only rendered while `isPlaying`, so a producer who
                    clicked the ruler couldn't see where the transport
                    would resume from. The line now reads Tone.Transport
                    in an RAF loop (regardless of play state) and paints
                    itself, so ruler clicks are visibly confirmed. */}
                <PlayheadLine pixelsPerBar={pixelsPerBar} trackId={track.id} />

                {loop && (
                  <div
                    className="absolute top-0 bottom-0 pointer-events-none border-l-2 border-r-2"
                    style={{
                      left: loop.start * pixelsPerBar,
                      width: (loop.end - loop.start) * pixelsPerBar,
                      borderColor: "#3B82F6",
                      background: "linear-gradient(180deg, rgba(59,130,246,0.08), rgba(59,130,246,0.03))",
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}

        {/* v19.15 — MASTER TRACK ROW. Sits at the bottom of the grid,
            visually separated. Clicking it (or the header) routes the
            bottom Plugin panel to the Master FX chain via
            setActivePlugin("master:chain"). Regular track FX vs Master
            FX now have complete visual separation — no more collision
            when the producer alternates between them. */}
        {tracks.length > 0 && (() => {
          const isMasterActive = typeof activePlugin === "string" && activePlugin.startsWith("master:");
          const mfx = masterFxChain || [];
          return (
            <div
              className={`track-row flex relative border-t-2 border-[#00FF41]/40 ${isMasterActive ? "bg-[#0a1a10]" : ""}`}
              style={{ height: trackHeight }}
              onClick={() => setActivePlugin("master:chain")}
              data-testid="master-track-row"
            >
              <div
                className={`w-[210px] flex-shrink-0 flex flex-col justify-center gap-1 px-2 py-1.5 border-r border-[#222] sticky left-0 z-10 cursor-pointer ${isMasterActive ? "ring-2 ring-inset ring-[#00FF41]" : ""}`}
                style={{
                  borderLeft: `3px solid #00FF41`,
                  background: isMasterActive
                    ? "linear-gradient(180deg, #062017, #041a10)"
                    : "linear-gradient(180deg, #1a1a1a, #0e0e0e)",
                }}
                onClick={(e) => { e.stopPropagation(); setActivePlugin("master:chain"); }}
                data-testid="master-track-header"
                title="Click to open the Master FX chain in the Plugin panel"
              >
                <div className="flex items-center gap-1.5 w-full">
                  <span
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ background: "#00FF41", boxShadow: "0 0 6px #00FF41" }}
                  />
                  <span className="text-[12px] leading-tight font-extrabold tracking-widest text-[#00FF41]">MASTER</span>
                </div>
                <div className="flex items-center gap-1 w-full pl-[18px]">
                  <div className="text-[9px] font-mono text-white/40 flex items-center gap-1 flex-1 min-w-0 truncate">
                    <span className="truncate">Master bus · vol {Math.round((masterVolume || 0.8) * 100)}%</span>
                    {mfx.length > 0 && (
                      <span
                        className="px-1 rounded bg-[#00FF41]/20 border border-[#00FF41]/40 text-[#00FF41] font-bold cursor-pointer flex-shrink-0"
                        onClick={(e) => { e.stopPropagation(); setActivePlugin("master:chain"); }}
                        data-testid="master-fx-count"
                      >
                        FX×{mfx.length}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div
                className="flex relative"
                style={{
                  width: totalBars * pixelsPerBar,
                  background: isMasterActive
                    ? "repeating-linear-gradient(90deg, rgba(0,255,65,0.04) 0, rgba(0,255,65,0.04) 1px, transparent 1px, transparent 24px)"
                    : "repeating-linear-gradient(90deg, rgba(255,255,255,0.02) 0, rgba(255,255,255,0.02) 1px, transparent 1px, transparent 24px)",
                }}
                onClick={(e) => { e.stopPropagation(); setActivePlugin("master:chain"); }}
                data-testid="master-track-grid"
              >
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span
                    className="text-[10px] font-mono uppercase tracking-[0.25em]"
                    style={{ color: isMasterActive ? "#00FF41" : "rgba(255,255,255,0.25)" }}
                  >
                    {mfx.length === 0
                      ? "Click to add Master FX (upward clipper, EQ…) →"
                      : `Master chain: ${mfx.map((f) => f.plugin).join(" → ")}`}
                  </span>
                </div>
                <PlayheadLine pixelsPerBar={pixelsPerBar} trackId="master" />
              </div>
            </div>
          );
        })()}
        {marquee && (
          <div
            className="absolute pointer-events-none z-30"
            style={{
              left: Math.min(marquee.x1, marquee.x2),
              top: Math.min(marquee.y1, marquee.y2),
              width: Math.abs(marquee.x2 - marquee.x1),
              height: Math.abs(marquee.y2 - marquee.y1),
              background: "rgba(59, 130, 246, 0.18)",
              border: "1.5px solid rgba(59, 130, 246, 0.9)",
              boxShadow: "0 0 0 1px rgba(255,255,255,0.15) inset, 0 0 12px rgba(59,130,246,0.4)",
              borderRadius: 2,
            }}
            data-testid="marquee-box"
          />
        )}
      </div>
    </div>
  );
}

function transposeNote(pitch, semis) {
  const names = ["C","C#","D","D#","E","F","F#","G","G#","A","A#","B"];
  const m = pitch.match(/^([A-G]#?)(-?\d+)$/);
  if (!m) return pitch;
  const idx = names.indexOf(m[1]) + (parseInt(m[2], 10) + 1) * 12;
  const newIdx = Math.max(0, Math.min(127, idx + semis));
  const oct = Math.floor(newIdx / 12) - 1;
  const name = names[newIdx % 12];
  return `${name}${oct}`;
}

// v19.16 P0 — React.memo'd Waveform. Peaks are immutable per clip, so
// once the canvas is painted there's no reason to re-render it every
// time the parent (arranger row) re-renders. Custom equality checks
// the peaks reference + duration/offset/lengthSec + zoom + reversed
// so the DOM stays stable while playback is running.
//
// v19.27 — HIGH-DEFINITION CONTINUOUS WAVEFORM
// ---------------------------------------------
// Peaks now arrive as a dense Float32Array of INTERLEAVED [min, max]
// pairs (see `buildPeakPairs` in decodeAudio.js). At draw time we
// walk the visible slice one canvas pixel column at a time,
// aggregate every peak-pair that maps into that column into a single
// (min,max) envelope, and paint a 1-px-wide fillRect from
// `centerY - max*maxAmp` to `centerY - min*maxAmp`. That produces
// the fine, Logic/Ableton-style waveform with visible transients and
// dynamics — no blocky bars.
//
// LEGACY COMPATIBILITY: older clips saved with the positive-only
// single-value peaks array (Number[]) still render correctly — we
// detect the format via `peaksFormat === "pairs"` (or a Float32Array
// heuristic) and, for the legacy path, mirror each value symmetrically
// around center.
//
// CANVAS SETUP:
//   - `position: absolute; inset: 0` on the container so the canvas
//     inherits the clip's real pixel dimensions.
//   - `imageSmoothingEnabled = false` for sharp pixel-level lines.
//   - DPR-aware backing store (canvas.width = w * dpr).
//   - ResizeObserver triggers re-draw on zoom / trim / stretch.
//   - First paint fires synchronously the moment peaks arrive.
const Waveform = React.memo(function Waveform({ peaks, peaksFormat, offset = 0, duration, lengthSec, stretched = false, reversed = false }) {
  const containerRef = React.useRef(null);
  const canvasRef = React.useRef(null);

  React.useEffect(() => {
    if (!peaks || !peaks.length) return;
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    // Format detection. `pairs` = interleaved Float32Array of
    // [min0,max0,min1,max1,…]. Legacy = positive-only Number[]/
    // Float32Array of single values.
    const isPairs = peaksFormat === "pairs" || (peaks instanceof Float32Array && peaks.length >= 2 && (peaks[0] < 0 || peaks[1] > 0 || peaks.length % 2 === 0));

    const draw = () => {
      const rect = container.getBoundingClientRect();
      const w = Math.max(1, Math.floor(rect.width));
      const h = Math.max(1, Math.floor(rect.height));
      if (h < 2 || w < 2) return; // container not laid out yet
      const dpr = Math.max(1, window.devicePixelRatio || 1);
      if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
        canvas.width = w * dpr;
        canvas.height = h * dpr;
        canvas.style.width = `${w}px`;
        canvas.style.height = `${h}px`;
      }
      const ctx = canvas.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Sharp pixel-level lines — no anti-aliased blur across rows.
      ctx.imageSmoothingEnabled = false;
      ctx.clearRect(0, 0, w, h);

      // Determine the slice of peaks that maps to the visible audio
      // portion of the clip. Trimmed clips crop; stretched clips use
      // the whole peaks array across the new visual width.
      const totalPairs = isPairs ? (peaks.length / 2) : peaks.length;
      let startIdx = 0;
      let endIdx = totalPairs;
      if (!stretched && duration && duration > 0 && lengthSec && lengthSec > 0) {
        startIdx = Math.max(0, Math.floor((offset / duration) * totalPairs));
        endIdx = Math.min(totalPairs, Math.ceil(((offset + lengthSec) / duration) * totalPairs));
        if (endIdx <= startIdx) endIdx = Math.min(totalPairs, startIdx + 1);
      }
      const sliceLen = endIdx - startIdx;
      if (sliceLen <= 0) return;

      // Global-peak scan for auto-gain so quiet loops still show
      // legible dynamics. Bounded to 1.0 so already-hot audio isn't
      // clamped into distortion visually.
      let absMax = 0;
      if (isPairs) {
        for (let i = startIdx; i < endIdx; i++) {
          const mn = peaks[i * 2];
          const mx = peaks[i * 2 + 1];
          if (-mn > absMax) absMax = -mn;
          if (mx > absMax) absMax = mx;
        }
      } else {
        for (let i = startIdx; i < endIdx; i++) if (peaks[i] > absMax) absMax = peaks[i];
      }
      // Normalise so the loudest sample fills 90% of half-height —
      // leaves a hairline of clip margin so waves never smash into
      // the clip border.
      const norm = absMax > 0.001 ? (1 / absMax) : 0;
      const centerY = h / 2;
      const maxAmp = h * 0.45;

      ctx.fillStyle = "rgba(255,255,255,0.94)";

      // One canvas pixel column per x — aggregate every peak-pair
      // that maps into that column into a (min,max) envelope. This is
      // the core of the "continuous" render: pixel-per-column density
      // means every transient shows.
      const pairsPerPx = sliceLen / w;
      for (let x = 0; x < w; x++) {
        // Reversed = mirror the column-to-slice mapping left↔right so
        // the preview visually flips when the clip is reversed.
        const columnIndex = reversed ? (w - 1 - x) : x;
        let sStart = Math.floor(startIdx + columnIndex * pairsPerPx);
        let sEnd = Math.floor(startIdx + (columnIndex + 1) * pairsPerPx);
        if (sEnd <= sStart) sEnd = sStart + 1;
        if (sEnd > endIdx) sEnd = endIdx;

        let mn = 0, mx = 0;
        if (isPairs) {
          for (let i = sStart; i < sEnd; i++) {
            const v0 = peaks[i * 2];
            const v1 = peaks[i * 2 + 1];
            if (v0 < mn) mn = v0;
            if (v1 > mx) mx = v1;
          }
        } else {
          // Legacy positive-only: mirror around center.
          for (let i = sStart; i < sEnd; i++) {
            const v = peaks[i];
            if (v > mx) mx = v;
          }
          mn = -mx;
        }
        const top = centerY - mx * norm * maxAmp;
        const bot = centerY - mn * norm * maxAmp;
        // Guarantee at least a 1-pixel-tall trace even at total
        // silence so the clip never looks empty.
        let barH = Math.max(1, bot - top);
        ctx.fillRect(x, top, 1, barH);
      }
    };

    // First paint synchronously — peaks are already resolved by the
    // time this effect runs, so the waveform is visible on the very
    // next paint tick.
    draw();

    // Re-draw on every container resize (zoom, trim, stretch, row
    // height change) so the waveform tracks layout without stale
    // pixels.
    let ro = null;
    try {
      ro = new ResizeObserver(draw);
      ro.observe(container);
    } catch { /* browsers without ResizeObserver keep the first paint */ }
    return () => { try { ro && ro.disconnect(); } catch {} };
  }, [peaks, peaksFormat, offset, duration, lengthSec, stretched, reversed]);

  if (!peaks || !peaks.length) return null;
  return (
    <div
      ref={containerRef}
      style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}
      data-testid="clip-waveform-container"
    >
      <canvas ref={canvasRef} style={{ display: "block" }} />
    </div>
  );
}, (a, b) => (
  a.peaks === b.peaks && a.peaksFormat === b.peaksFormat && a.offset === b.offset && a.duration === b.duration && a.lengthSec === b.lengthSec && a.stretched === b.stretched && a.reversed === b.reversed
));

function GhostNotes({ track, region }) {
  // v13.0 BIRD'S-EYE MINIMAP:
  //  - Canvas is positioned absolutely inside its parent (which is
  //    `position:relative min-h-0`) so it fills exactly the bubble's
  //    content area and never grows past it.
  //  - x-axis is normalized against the REGION LENGTH (region.length * 16
  //    sixteenth-note steps) rather than min/max of the notes, so a melody
  //    that lives only in bar 1 of a 4-bar region shows up in the first
  //    quarter of the bubble instead of stretched across the full width.
  //  - Redraw is throttled via requestAnimationFrame inside a
  //    ResizeObserver callback to eliminate the "ResizeObserver loop
  //    completed with undelivered notifications" warning.
  const r = region || {};
  const canvasRef = useRef(null);
  const [themeState, setThemeState] = useState(() => document.documentElement.getAttribute("data-theme") || "dark");
  useEffect(() => {
    const on = () => setThemeState(document.documentElement.getAttribute("data-theme") || "dark");
    window.addEventListener("crow-theme-change", on);
    const mo = new MutationObserver(on);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => { window.removeEventListener("crow-theme-change", on); mo.disconnect(); };
  }, []);

  // Memoize the normalized coord list — only recomputes when notes/pattern
  // or region-length actually change.
  const normalizedNotes = React.useMemo(() => {
    const regionSteps = Math.max(1, Math.round((r.length || 1) * 16));
    if (track.type === "synth" || track.type === "sampler") {
      const notes = r.notes || [];
      if (notes.length === 0) return null;
      const names = ["C","C#","D","D#","E","F","F#","G","G#","A","A#","B"];
      const parse = (p) => {
        const m = p.match(/^([A-G]#?)(-?\d+)$/);
        if (!m) return 60;
        return names.indexOf(m[1]) + (parseInt(m[2], 10) + 1) * 12;
      };
      const midiVals = notes.map((n) => parse(n.pitch));
      const minPitch = Math.min(...midiVals);
      const maxPitch = Math.max(...midiVals);
      const pitchRange = Math.max(1, maxPitch - minPitch + 1);
      return {
        kind: "notes",
        entries: notes.map((n, i) => ({
          // x normalized against the whole region, not the note-span.
          x: (n.step || 0) / regionSteps,
          w: (n.duration || 1) / regionSteps,
          y: (maxPitch - midiVals[i]) / pitchRange,
          h: 1 / pitchRange,
        })),
      };
    }
    // Drum pattern branch — 16 equal steps mapped to the FIRST BAR of the
    // region; the rest of the bubble stays empty so a 4-bar drum region
    // visually shows the 1-bar pattern repeating conceptually at bar 1.
    const pattern = r.pattern || [];
    const active = pattern.map((on, i) => on ? i : -1).filter((i) => i >= 0);
    if (active.length === 0) return null;
    // Drum patterns are 16 steps = 1 bar; show them proportionally across
    // whatever fraction of the region 1 bar occupies.
    const regionBars = Math.max(1, r.length || 1);
    return {
      kind: "pattern",
      entries: active.map((i) => ({
        x: (i / 16) / regionBars,
        w: (1 / 16) / regionBars,
        y: 0.4,
        h: 0.2,
      })),
    };
  }, [r.notes, r.pattern, r.length, track.type]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !normalizedNotes) return;
    let rafId = 0;
    const draw = () => {
      rafId = 0;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (w === 0 || h === 0) return;
      const dpr = window.devicePixelRatio || 1;
      const bw = Math.max(1, Math.round(w * dpr));
      const bh = Math.max(1, Math.round(h * dpr));
      if (canvas.width !== bw) canvas.width = bw;
      if (canvas.height !== bh) canvas.height = bh;
      const ctx = canvas.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      const padY = 3;
      const drawH = Math.max(2, h - padY * 2);
      ctx.fillStyle = themeState === "light" ? "rgba(15,15,15,0.85)" : "rgba(255,255,255,0.85)";
      for (const e of normalizedNotes.entries) {
        const px = e.x * w;
        const pw = Math.max(1, e.w * w * 0.9);
        const py = padY + e.y * drawH;
        const ph = Math.max(2, e.h * drawH);
        ctx.fillRect(px, py, pw, ph);
      }
    };
    const scheduleDraw = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(draw);
    };
    scheduleDraw();
    // Wrap the ResizeObserver callback in RAF to avoid the
    // "ResizeObserver loop completed with undelivered notifications"
    // browser warning that fires when synchronous work in the callback
    // triggers another layout in the same frame.
    const ro = new ResizeObserver(() => scheduleDraw());
    ro.observe(canvas);
    return () => {
      ro.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [normalizedNotes, themeState]);

  if (!normalizedNotes) return null;
  return (
    <canvas
      ref={canvasRef}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block", pointerEvents: "none" }}
      data-testid={`ghost-notes-${r.id || ""}`}
    />
  );
}

function LoopBar({ loop, setLoop, totalBars, pixelsPerBar }) {
  const handleMouseDown = (e, mode) => {
    e.stopPropagation();
    const startX = e.clientX;
    const startLoop = { ...loop };
    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dBars = Math.round(dx / pixelsPerBar);
      if (mode === "move") {
        const len = startLoop.end - startLoop.start;
        const ns = Math.max(0, Math.min(totalBars - len, startLoop.start + dBars));
        setLoop({ start: ns, end: ns + len });
      } else if (mode === "left") {
        const ns = Math.max(0, Math.min(startLoop.end - 1, startLoop.start + dBars));
        setLoop({ start: ns, end: startLoop.end });
      } else if (mode === "right") {
        const ne = Math.max(startLoop.start + 1, Math.min(totalBars, startLoop.end + dBars));
        setLoop({ start: startLoop.start, end: ne });
      }
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return (
    <div
      className="absolute top-0 bottom-0 cursor-grab active:cursor-grabbing"
      style={{
        left: loop.start * pixelsPerBar,
        width: (loop.end - loop.start) * pixelsPerBar,
        background: "linear-gradient(180deg, #3B82F6, #1d4ed8)",
        borderRadius: 2,
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.3), 0 0 8px rgba(59,130,246,0.6)",
      }}
      onMouseDown={(e) => handleMouseDown(e, "move")}
      data-testid="loop-region"
      title="Drag to move · grab edges to resize · double-click to clear"
    >
      <div
        className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-white/40"
        onMouseDown={(e) => handleMouseDown(e, "left")}
      />
      <div
        className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-white/40"
        onMouseDown={(e) => handleMouseDown(e, "right")}
      />
    </div>
  );
}

// v13.1 — AUTOMATION LANE. Renders inside a track lane when the track's
// automation.volume.enabled flag is true.
//  - A horizontal reference line sits at the vertical middle of the lane;
//    that reference line is the current static track.volume value
//    (i.e. "unity" for the automation curve). Nodes above the line push
//    gain higher, below push it lower — value ∈ [0, 1] linear gain.
//  - Click the empty lane to drop a new node at that (bar, value).
//  - Drag any node to reposition it (bar horizontally, value vertically).
//  - Right-click a node to delete it.
//  - Consecutive nodes are connected with a green polyline that mirrors
//    exactly what the engine's RAF loop interpolates.
function AutomationLane({ track, pixelsPerBar, totalBars, trackHeight, onAddNode, onUpdateNode, onRemoveNode }) {
  const nodes = track.automation?.volume?.nodes || [];
  const width = totalBars * pixelsPerBar;
  const height = trackHeight;
  // Convert linear gain [0,1] → y (0 at top, height at bottom, unity mid-line).
  const valueToY = (v) => (1 - Math.max(0, Math.min(1, v))) * height;
  const yToValue = (y) => Math.max(0, Math.min(1, 1 - y / height));

  const laneRef = useRef(null);
  const [dragIdx, setDragIdx] = useState(null);

  const posFromEvent = (e) => {
    const rect = laneRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(width, e.clientX - rect.left));
    const y = Math.max(0, Math.min(height, e.clientY - rect.top));
    return { bar: x / pixelsPerBar, value: yToValue(y), x, y };
  };

  const handleLaneClick = (e) => {
    // Only handle direct clicks on the SVG background (not on nodes).
    if (e.target !== laneRef.current) return;
    e.stopPropagation();
    const { bar, value } = posFromEvent(e);
    onAddNode(bar, value);
  };

  const handleNodeDown = (e, idx) => {
    e.stopPropagation();
    e.preventDefault();
    setDragIdx(idx);
    const onMove = (ev) => {
      const { bar, value } = posFromEvent(ev);
      onUpdateNode(idx, { bar: Math.max(0, bar), value });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setDragIdx(null);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const points = nodes
    .map((n) => `${(n.bar || 0) * pixelsPerBar},${valueToY(n.value)}`)
    .join(" ");

  return (
    <svg
      ref={laneRef}
      className="absolute inset-0 z-30 cursor-crosshair"
      width={width}
      height={height}
      onMouseDown={handleLaneClick}
      data-testid={`automation-lane-${track.id}`}
      style={{ pointerEvents: "auto" }}
    >
      {/* 0dB reference line at unity gain (value=0.8, matching the default
          static track.volume so the line represents the current fader). */}
      <line
        x1={0}
        y1={valueToY(0.8)}
        x2={width}
        y2={valueToY(0.8)}
        stroke="rgba(255,255,255,0.25)"
        strokeDasharray="4 4"
        strokeWidth={1}
        pointerEvents="none"
      />
      {/* Node polyline */}
      {nodes.length >= 2 && (
        <polyline points={points} fill="none" stroke="#10B981" strokeWidth={2} pointerEvents="none" />
      )}
      {/* Nodes */}
      {nodes.map((n, i) => (
        <circle
          key={i}
          cx={(n.bar || 0) * pixelsPerBar}
          cy={valueToY(n.value)}
          r={dragIdx === i ? 6 : 5}
          fill="#10B981"
          stroke="#fff"
          strokeWidth={1.5}
          style={{ cursor: "grab" }}
          onMouseDown={(e) => handleNodeDown(e, i)}
          onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); onRemoveNode(i); }}
          data-testid={`automation-node-${track.id}-${i}`}
        />
      ))}
    </svg>
  );
}


// v14.0.1 — Isolated 60Hz Playhead. Reads Tone.Transport directly (not
// gated on engine.initialized so ruler clicks before first play still
// paint the green line) and writes only its own inline transform —
// never triggers a React re-render on the parent tree. Wrapped in
// React.memo so a parent re-render (e.g. adding a track) doesn't kill
// the RAF loop by unmounting the element.
// v19.15 — Also paints when the transport is stopped so users can see
// exactly where playback will resume from after clicking the ruler.
// P2 optimization: the RAF loop suspends automatically when the tab is
// hidden (document.visibilityState==="hidden") so background tabs
// don't burn CPU on invisible playhead updates. Resumes on visibility
// change.
const PlayheadLine = React.memo(function PlayheadLine({ pixelsPerBar, trackId }) {
  const ref = useRef(null);
  useEffect(() => {
    let raf = 0;
    let stopped = false;
    const tick = () => {
      if (stopped) return;
      if (document.visibilityState === "hidden") {
        // Skip the frame; check again in ~200ms.
        raf = setTimeout(() => { raf = requestAnimationFrame(tick); }, 200);
        return;
      }
      if (ref.current) {
        try {
          const t = Tone.getTransport();
          const sec = t.seconds || 0;
          const bpm = (t.bpm && t.bpm.value) || 120;
          const bars = (sec * bpm) / 60 / 4;
          ref.current.style.transform = `translateX(${bars * pixelsPerBar}px)`;
        } catch { /* transport not ready — leave at 0 */ }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const onVis = () => {
      // Force one tick right away on visibility restore so the line
      // snaps to its correct spot rather than drifting for a frame.
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stopped = true;
      if (raf) { try { cancelAnimationFrame(raf); } catch {} try { clearTimeout(raf); } catch {} }
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [pixelsPerBar]);
  return (
    <div
      ref={ref}
      className="absolute top-0 bottom-0 w-px bg-[#00FF41] pointer-events-none z-20"
      style={{ left: 0, boxShadow: "0 0 6px #00FF41, 0 0 12px #00FF41", willChange: "transform" }}
      data-testid={`playhead-${trackId}`}
    />
  );
});

